import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { X, PhoneOff, AlignLeft, Info as InfoIcon, Minus, Maximize2, Minimize2,
         ExternalLink, CornerDownLeft, Mail, Check, Loader2, Pencil, Ban, Sun, Moon } from 'lucide-react';
import { useStore } from '../store';
import type { CallWindowMode } from '../store/salesCrmSlice';
import { salesCrmApi } from '../services/salesCrmApi';
import { leadGenApi, CENTRAL_PERSON_KEY, type DomainLead, type LeadPhone } from '../services/leadGenApi';
import { fraud0ThemeClass } from './fraud0/lib/fraud0Theme';
import { stopCallRecording, useCallRecordingState } from './fraud0/lib/callRecording';
import { CallbackPicker, useCallbackPresets } from '../components/CallbackPicker';
import { labelFor } from '../lib/callbackPresets';
import { buildScriptTokenContext, fillPlaceholders } from '../lib/scriptTokens';
import { gehoertDemCockpit } from '../lib/callKeys';
import { skriptTeile } from '../lib/skriptTeile';
import { useFenster } from '../lib/fenster';
import { FensterGriffe } from '../lib/FensterGriffe';
import { useFensterEbene } from '../lib/fensterStapel';
import { AusgelagerterInhalt, useAusgelagertesFenster } from '../lib/fensterAuslagern';
import type { CallOutcome, CallOutcomeDefekt, CallOutcomeMeta, EmotionTag, LeadStatus, EffectiveScript, Lead, BlockMeta, BlockKind } from '../types';
import { CALL_OUTCOMES, CALL_OUTCOME_META, type MailWunschDetail } from '../types';

// -----------------------------------------------------------------------------
// CallCockpit: fokussierter, keyboard-gesteuerter Anruf.
// Ein Script-Abschnitt gross & lesbar im Fokus; per Tasten durch die Sektionen.
// Tracking (Dauer/Outcome/Emotion/Notizen) laeuft im Hintergrund und erst am
// Ende im Abschluss-Panel.
//
// Drei Formen, EIN Bauteil (2026-08-11, Nutzerentscheidung — vorher nur Vollbild):
//   'window' — Pop-up neben der uebrigen App. Standard. Verschiebbar, groessenaenderbar.
//   'full'   — das fruehere Vollbild, unveraendert. Bleibt ueber einen Knopf erreichbar.
//   'min'    — zusammengeklappt auf die kleine „Call laeuft"-Marke unten rechts.
//
// Entscheidend: das Bauteil bleibt in ALLEN dreien gemountet ({callViewOpen && <CallCockpit />}
// in AppShell.tsx). Waere „zusammenklappen" ein Unmount, verloere man Timer, Notizen, Outcome und
// die Skript-Position — also genau das, was der laufende Anruf gerade produziert.
//
// Die Tastenkuerzel (W/L/F/N, G, B, S, X, Pfeile) haengen am ganzen Fenster. Das war harmlos,
// solange das Cockpit den Bildschirm allein hatte; als Pop-up NEBEN einer anderen Seite wuerde „X"
// (nicht erreicht → sofort speichern und beenden) auch feuern, waehrend man in der Telefon-Tabelle
// klickt. Im Fenster-Modus greifen sie deshalb nur, wenn der Tastatur-Fokus IM Cockpit liegt
// (`rootRef.contains(document.activeElement)`); der Akzent-Rahmen zeigt das an. Im Vollbild bleibt
// alles wie bisher — dort gibt es kein Daneben.
// -----------------------------------------------------------------------------

// Lage und Groesse des Fensters ueberleben den Neustart: wer es einmal dorthin geschoben hat, wo
// sein zweiter Bildschirm anfaengt, will es dort jeden Morgen wiederfinden.
//
// Die Mechanik dahinter (Klemmen, Merken, Ziehen, Groessenaenderung) steht seit 2026-08-11 in
// `lib/fenster.ts` — sie wird auch vom Details- und Notiz-Fenster der Telefon-Tabelle benutzt.
// Hier bleibt nur, was DIESES Fenster ausmacht: der Speicherschluessel und die drei Formen.
const GEOM_KEY = 'call_window_geom';

// Das Mail-Pult kommt samt Vorlagen-Skeletten und den beiden Vorlagen-Editoren — es NACHzuladen ist
// kein Feinschliff: das Cockpit haengt fest in der AppShell (nicht lazy wie die Seiten), sein Code
// laedt also bei jedem Seitenaufruf mit. Die Mail schreibt man in einem Bruchteil der Gespraeche.
const EmailOutreach = lazy(() => import('./fraud0/stack/EmailOutreach')
  .then((m) => ({ default: m.EmailOutreach })));

const EMOTIONS: EmotionTag[] = ['warm', 'neutral', 'cold', 'hostile'];
const EMOTION_LABEL: Record<EmotionTag, string> = { warm: 'Warm', neutral: 'Neutral', cold: 'Cold', hostile: 'Hostile' };
// Die zehn Ergebnisse, ihre Tasten, Farben und Folgen stehen in types/index.ts (CALL_OUTCOME_META).
// Hier bleibt nur die Aufteilung in die zwei Blöcke des Abschluss-Panels — bei neun Knöpfen in
// einer Reihe sucht man nach dem Gespräch, statt zu wählen.
const OUTCOME_GRUPPEN: { titel: string; outcomes: CallOutcome[] }[] = [
  { titel: 'Gespräch geführt', outcomes: CALL_OUTCOMES.filter((o) => CALL_OUTCOME_META[o].gruppe === 'gespraech') },
  { titel: 'Kein Gespräch',    outcomes: CALL_OUTCOMES.filter((o) => CALL_OUTCOME_META[o].gruppe === 'kein_gespraech') },
];
// Der GEWÄHLTE Ergebnis-Knopf. Bis 2026-08-21 trug jeder Ton eine eigene Farbe (Rot für den
// Anschluss-Befund, Blau für den Umweg, Grau für die Stille). Im monochromen Cockpit fällt das weg,
// und zwar nicht als Verlust: die Farbe kodierte etwas, das direkt daneben AUSGESCHRIEBEN steht —
// man liest „NUMMER NICHT VERGEBEN", man errät es nicht am Rotton. Gewählt heisst jetzt schlicht
// gefüllt, ungewählt umrissen. Ein Zustand, ein Zeichen.
const TON_SELECTED: Record<CallOutcomeMeta['ton'], string> = {
  gut: 'bg-accent border-accent', offen: 'bg-accent border-accent', schlecht: 'bg-accent border-accent',
  umweg: 'bg-accent border-accent', still: 'bg-accent border-accent', defekt: 'bg-accent border-accent',
};
// Lesegrößen des Skripts. Der Faktor multipliziert die Grundgrade (Fenster/Vollbild getrennt) und
// überlebt den Neustart — wer weitsichtig ist, stellt das EINMAL ein und nicht bei jedem Anruf neu.
// Sieben Stufen statt einer stufenlosen Regelung: im Gespräch will man zwei Tastendrücke, keinen
// Schieberegler, den man auf den Zehntelpunkt genau treffen muss.
const SCHRIFT_STUFEN = [0.75, 0.85, 1, 1.15, 1.3, 1.5, 1.75];
const SCHRIFT_STANDARD = 2;
// Termin-Buchung fürs Closing (Calendly). Bei Bedarf später konfigurierbar machen.
// Kontinuierliche Tastatur-Scroll-Geschwindigkeit (px pro Frame ≈ SCROLL_SPEED×60 px/s).
const SCROLL_SPEED = 10;

// Script-Body rendern: Zeilen, die mit „…"/„..." beginnen, sind die erwarteten Lead-Antworten
// (z.B. „… (Hallo?)") und werden grau dargestellt · klar getrennt von dem, was der Setter sagt.
//
// Was hier ankommt, ist bereits gefüllt (`fillPlaceholders`). Ein [Baustein], der TROTZDEM noch
// dasteht, hat keinen Wert bekommen — entweder gibt es zu diesem Lead nichts (kein Jobtitel, keine
// Nische) oder er ist von Hand gemeint (z. B. [anzeigentitel]). Beides heisst dasselbe: hier muss
// der Setter selbst etwas sagen. Deshalb steht er sichtbar da statt als Lücke — und deshalb ist er
// markiert: vorgelesen klingt „Sie sind ja eckige Klammer job" wie ein Fehler des Anrufers.
// Markdown-Unterüberschriften („### Who", „### Why") sind Gliederung FÜR DEN AUTOR des Skripts —
// im Gespräch sind sie Text, den man nicht spricht und über den das Auge trotzdem stolpert, weil er
// mitten im Vorlesestoff steht und aussieht wie ein Satz. Sie fliegen deshalb aus der Anrufansicht
// raus; in der Skript-Bibliothek stehen sie unverändert.
// Die Leerzeilen um sie herum verschwinden mit: bliebe die Lücke, hätte man statt der Überschrift
// ein Loch, und der Absatzabstand wäre plötzlich doppelt so gross wie überall sonst.
const UEBERSCHRIFT = /^\s{0,3}#{1,6}\s+\S/;
function ohneUeberschriften(text: string): string {
  return (text || '')
    .split('\n')
    .filter((l) => !UEBERSCHRIFT.test(l))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+/, '')
    .trimEnd();
}

function renderScript(text: string) {
  const lines = ohneUeberschriften(text).split('\n');
  return lines.map((line, i) => {
    const isLead = /^\s*(?:…|\.\.\.)/.test(line);
    const teile = line.split(/(\[[^\]]+\])/g);
    return (
      <span key={i} className={isLead ? 'text-text-muted' : undefined}>
        {teile.map((teil, j) => (
          teil.startsWith('[') && teil.endsWith(']')
            // Monochrom markiert: fett plus gestrichelte Unterlängslinie statt Gelb. Auffallen muss
            // die Lücke trotzdem — vorgelesen klingt „Sie sind ja eckige Klammer job" wie ein Fehler.
            ? <span key={j} className="font-semibold border-b-2 border-dashed border-text-muted/70"
                    title="Zu diesem Lead gibt es dafür keinen Wert — hier selbst etwas einsetzen">
                {teil}
              </span>
            : teil
        ))}
        {i < lines.length - 1 ? '\n' : ''}
      </span>
    );
  });
}

const CALENDLY_URL = 'https://calendly.com/felix-kellermann-fraud0/30min';
// Synthetische Schluss-Sektion: nach dem Close kommt direkt der eingebettete Calendly-Kalender.
const BOOKING_KEY = '__booking__';
// Unter dieser Dauer fragt der Abschluss nach, ob der Call überhaupt getrackt werden soll (Fehlwahl/kurz).
const SHORT_CALL_SECONDS = 20;
// Taste → Ergebnis, aus derselben Tabelle abgeleitet wie die Beschriftung. Zwei Ergebnisse mit
// derselben Taste fielen hier lautlos zusammen; deshalb prüft es die Entwicklungsfassung laut.
const OUTCOME_KEY: Record<string, CallOutcome> = Object.fromEntries(
  CALL_OUTCOMES.map((o) => [CALL_OUTCOME_META[o].hotkey.toLowerCase(), o]),
);
if (import.meta.env.DEV && Object.keys(OUTCOME_KEY).length !== CALL_OUTCOMES.length) {
  console.error('[CallCockpit] Doppelt belegte Outcome-Taste in CALL_OUTCOME_META');
}

const DEFEKT_NOTIZ: Record<CallOutcomeDefekt, string> = {
  falsche_nummer:   'Aus dem Anruf heraus als falsche Nummer gemeldet',
  nicht_vergeben:   'Aus dem Anruf heraus als nicht vergeben gemeldet',
  nicht_zustaendig: 'Aus dem Anruf heraus als nicht zuständig gemeldet',
};

// Welcher Befund aus dem Ergebnis an der gewählten Nummer abfällt — und wann eben NICHT.
// 'nicht_zustaendig' wirkt auf die Personenzeile (scope 'person'); an der ZENTRALE wäre das falsch:
// dort ist nie jemand „zuständig", die Zentrale ist der Weg zu den Zuständigen. Sie zu sperren
// nähme dem nächsten Anruf den einzigen Eingang ins Unternehmen.
function defektFuer(outcome: CallOutcome | null, personKey: string | null | undefined): CallOutcomeDefekt | null {
  const d = outcome ? CALL_OUTCOME_META[outcome].defekt : undefined;
  if (!d) return null;
  if (d === 'nicht_zustaendig' && personKey === CENTRAL_PERSON_KEY) return null;
  return d;
}

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Die Wiedervorlage-Zeitwahl (Vorschläge, Vorwahl, Feinwahl) liegt seit 2026-08-10 in
// components/CallbackPicker.tsx — dieselbe Wahl steht jetzt auch auf der fraud0-Lead-Karte.

// Art eines Blocks, in drei Stufen — jede genauer als die nächste:
//   1. `block_meta.kind` der Main — die gepflegte Struktur-Achse (Block-Editor, admin/HoS).
//   2. `sec.kind` am Block selbst — kommt mit, wenn das Skript als Dokument gereist ist: die
//      Skript-Bibliothek trägt seit 2026-08-17 zwei Bereiche (Ablauf | Einwände), und ein
//      übernommenes Bibliotheks-Skript bringt Blöcke mit, die die Main gar nicht kennt. Ohne
//      diese Stufe fielen genau die auf die Heuristik zurück — die Einwände landeten im Ablauf
//      und wurden im Call der Reihe nach vorgelesen.
//   3. die frühere Heuristik über Key/Titel — Rückfall für Bestandsskripte ohne jeden Vermerk.
// Warum die Reihenfolge so und nicht anders: die Main ist die gemeinsame Fassung und schlägt das
// mitgebrachte Dokument; ein ausdrücklicher Vermerk schlägt in jedem Fall eine Namensregel.
function kindOf(key: string, sec: { title?: string; kind?: BlockKind } | undefined,
                meta?: BlockMeta): BlockKind {
  if (meta?.kind) return meta.kind;
  if (sec?.kind) return sec.kind;
  const k = (key || '').toLowerCase();
  const t = (sec?.title || '').toLowerCase();
  if (k.startsWith('einwand') || k === 'notfall' || t.startsWith('einwand') || t.startsWith('notfall')) return 'objection';
  return 'flow';
}

// Ein Abzweigungs-Titel zerfällt in Rubrik und Sache: „Ablehnung: Kein Interesse" → „Ablehnung"
// + „Kein Interesse". Beide Schreibweisen kommen im Bestand vor, mit Doppelpunkt (Bibliothek) und
// mit Mittelpunkt (ältere Forks).
//
// Bis 2026-08-21 trennte das nur am Mittelpunkt — mit dem Ergebnis, dass in der Anrufansicht auf
// achtzehn Knöpfen sechsmal „Ablehnung:" und sechsmal „Einwand:" stand. Das Präfix nahm die halbe
// Knopfbreite, und abgeschnitten wurde ausgerechnet das Ende, an dem die Einwände sich
// unterscheiden („Woher hast du meine Nu…" / „Das ist aber meine privat…"). Die Rubrik gehört
// EINMAL über die Gruppe, nicht achtzehnmal in die Zeile.
//
// Die Obergrenze von 24 Zeichen hält versehentliche Treffer fern: ein Doppelpunkt mitten in einem
// Satz („Kurz gesagt: …") ist keine Rubrik, sondern Satzzeichen.
function branchTeile(title: string): { rubrik: string | null; sache: string } {
  const t = (title || '').trim();
  const m = t.match(/^([^:·]{2,24}?)\s*[:·]\s*(\S.*)$/);
  if (m) return { rubrik: m[1].trim(), sache: m[2].trim() };
  return { rubrik: null, sache: t || 'Einwand' };
}
function branchLabel(title: string): string {
  return branchTeile(title).sache;
}

// Ablehnung oder Einwand? Die Unterscheidung steht im Titel und nirgends sonst — `block_meta.group`
// ist bei allen Blöcken „einwaende", das trennt nichts. Sie ist trotzdem keine Spitzfindigkeit:
// eine Ablehnung ist der Reflex in den ersten zehn Sekunden („woher hast du meine Nummer"), ein
// Einwand die Sache, über die man verhandelt, wenn das Gespräch schon läuft. Wer im zweiten steckt,
// bekommt das erste nie mehr zu hören.
function istAblehnung(titel: string): boolean {
  return /^ablehn/i.test(branchTeile(titel).rubrik ?? '');
}

// Zwei Tastenreihen statt einer, ab 2026-08-21.
//
// Vorher teilten sich Ablehnungen und Einwände die Ziffern 1–9, in Anzeigereihenfolge durchgezählt.
// Bei vierzehn Abzweigungen hiess das: neun bedienbar, fünf nur per Maus — und WELCHE neun hing
// daran, wie viele Ablehnungen zufällig vorher standen. Getrennte Reihen machen die Belegung
// stabil: die Ziffer eines Einwands ändert sich nicht mehr, weil eine Ablehnung dazukommt.
//
// Ziffern für die Einwände, Buchstaben für die Ablehnungen — nicht andersherum. Zwei Gründe: die
// Einwände sind die grössere Gruppe (sieben gegen sechs, plus die unrubrizierten) und passen damit
// besser in die neun Ziffern; und die Ziffern lagen schon immer auf den Einwänden, das bleibt so.
//
// Das I fehlt in der Buchstabenreihe mit Absicht: es öffnet die Kontaktdaten. Ein Kürzel, das je
// nach Skript mal Info und mal „Ist das ein Cold Call" bedeutet, ist schlimmer als eins weniger.
// Die Reihe wird nicht abgezählt, sondern abgelesen — der Buchstabe steht auf der Karte.
const TASTEN_ABLEHNUNG = ['q', 'w', 'e', 'r', 't', 'z', 'u', 'o', 'p'];
const TASTEN_EINWAND = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

// Die Abzweigungen nach Rubrik bündeln, ohne die Reihenfolge des Skripts zu verwerfen: Gruppen
// erscheinen in der Reihenfolge ihres ersten Auftretens, innerhalb einer Gruppe bleibt die
// Skript-Ordnung. Ohne Rubrik landet alles zusammen in „Weitere" — und zwar ans Ende, weil das
// die Blöcke sind, die niemand benannt hat.
//
// Wichtig: das Ergebnis ist zugleich die TASTENreihenfolge (1–9). Gruppierte Anzeige und
// ungruppierte Tastenbelegung wären eine Falle — die Ziffer auf dem Knopf muss die Ziffer sein,
// die ihn auch auslöst.
function gruppiereBranches(keys: string[], titelVon: (k: string) => string):
    { rubrik: string | null; keys: string[] }[] {
  const gruppen: { rubrik: string | null; keys: string[] }[] = [];
  for (const k of keys) {
    const r = branchTeile(titelVon(k)).rubrik;
    const treffer = gruppen.find((g) => g.rubrik === r);
    if (treffer) treffer.keys.push(k);
    else gruppen.push({ rubrik: r, keys: [k] });
  }
  return [...gruppen.filter((g) => g.rubrik), ...gruppen.filter((g) => !g.rubrik)];
}

// Rückfall für Skripte ohne block_meta.objections (ältere Forks, Bianca-Bestand).
// Unbekannte Sektion → alle vorhandenen Einwände anbieten.
const LEGACY_SECTION_OBJECTIONS: Record<string, string[]> = {
  opener: ['einwand_wer', 'einwand_nummer', 'notfall'],
  verifizierung: ['einwand_ansprechpartner', 'einwand_wer', 'einwand_nummer'],
  hook: ['einwand_funktion', 'notfall'],
  proof: ['einwand_funktion', 'notfall'],
  close: ['einwand_ansprechpartner', 'einwand_funktion'],
};

// Für die aktuelle Sektion die passenden, tatsächlich vorhandenen Einwände (in definierter Reihenfolge).
// Reihenfolge: block_meta.objections der Bibliothek → LEGACY_SECTION_OBJECTIONS → alle Abzweigungen.
//
// Trifft eine Zuordnung KEINEN einzigen vorhandenen Einwand, gelten wieder alle. Das ist kein
// Schönheitsfehler, sondern der Fall, der seit den zwei Bereichen (2026-08-17) häufig wird: ein
// übernommenes Bibliotheks-Skript bringt eigene Einwände mit, seine Ablauf-Blöcke heissen aber
// womöglich wie die der alten Karte („opener"). Gefiltert bliebe dann NICHTS übrig — der Setter
// stünde mit einem Skript voller Einwandantworten da, von denen keine erreichbar ist. Lieber alle
// anbieten als keinen: eine zu lange Knopfreihe ist eine Unbequemlichkeit, eine leere ein Verlust.
function objectionsForSection(sectionKey: string, branchKeys: string[], meta?: Record<string, BlockMeta>): string[] {
  const fromMeta = meta?.[sectionKey]?.objections;
  const mapped = (fromMeta && fromMeta.length ? fromMeta : LEGACY_SECTION_OBJECTIONS[sectionKey]);
  if (!mapped) return branchKeys;
  const treffer = mapped.filter((k) => branchKeys.includes(k));
  return treffer.length ? treffer : branchKeys;
}

// Kleiner Status-Hinweis in "Letzte Calls" — kein Player, kein Transkript-Text (bewusst schlank,
// Nachlesen läuft über Discord #call-review, siehe past-plans/ich-will-bei-jedem-logical-finch.md).
// Fragt den Status pro sichtbarem Call einmal ab; 404 (kein Upload, z.B. fehlende
// Mikro-Berechtigung) zeigt schlicht nichts an.
function AufnahmeStatusChip({ callId }: { callId: number }) {
  const [status, setStatus] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    salesCrmApi.getCallRecordingStatus(callId)
      .then((r) => { if (live) setStatus(r.status); })
      .catch(() => { /* keine Aufnahme zu diesem Call — kein Fehlerzustand */ });
    return () => { live = false; };
  }, [callId]);
  if (!status) return null;
  const label = status === 'ready' ? '🎙 transkribiert'
    : status === 'failed' ? '🎙 Transkription fehlgeschlagen'
    : '🎙 wird transkribiert…';
  return <div className="text-text-muted/70 text-xs mt-0.5">{label}</div>;
}

export default function CallCockpit() {
  const {
    activeLead, activeVariant, personalizedScript, salesScript,
    callTimerStart, endCall, addCall, updateLead, user,
    linkedinAccounts, selectedLinkedInAccountId,
    calls, fetchCalls, fetchLinkedInAccounts,
    callWindowMode, setCallWindowMode, aktuelleWahl,
  } = useStore();

  // ── Fenster, Vollbild oder zusammengeklappt ─────────────────────────────────────────────────
  // Hat das Cockpit den Tastatur-Fokus? Steuert die Kürzel (s. Kopfnotiz) und den Rahmen, der es
  // sichtbar macht — ein unsichtbarer Zustand, der Tasten schluckt, wäre schlimmer als keiner.
  const [aktiv, setAktiv] = useState(true);
  // `imFenster` kommt aus dem Haken: Wunsch UND genug Bildschirm. Auf einem schmalen Gerät fällt
  // das Cockpit damit von selbst auf Vollbild zurück — dort ist das die einzige Form, in der ein
  // Skript lesbar bleibt.
  const { schmal, imFenster, rootRef, ziehGriff, griffe, fensterStil } =
    useFenster({ key: GEOM_KEY, gewuenschtImFenster: callWindowMode === 'window' });
  // Gemeinsamer Fenster-Stapel mit den Nachschlage-Fenstern der Telefon-Tabelle: zuletzt angetippt
  // liegt oben. Vorher lagen die immer ueber dem Cockpit — wer das Skript wiedersehen wollte,
  // musste sie wegschieben oder schliessen.
  const { ebene, nachVorn } = useFensterEbene();

  const modus: CallWindowMode = schmal && callWindowMode === 'window' ? 'full' : callWindowMode;
  const ausgelagert = modus === 'popout';
  // Schliesst der Benutzer das ausgelagerte Fenster, kommt das Cockpit in den Tab zurueck — der
  // Anruf laeuft ja weiter. Ihn dabei zu verlieren waere der einzige wirklich teure Ausgang.
  const zurueckInDenTab = useCallback(() => setCallWindowMode('window'), [setCallWindowMode]);
  const { fenster: popFenster, blockiert } = useAusgelagertesFenster({
    offen: ausgelagert, titel: 'Anruf — Leitkraft', breite: 980, hoehe: 820,
    name: 'lk_call_cockpit', onZu: zurueckInDenTab,
  });
  // Der Popup-Blocker hat zugeschlagen: nicht stumm im Nichts stehen bleiben, sondern zurueck in
  // den Tab und es sagen. Sonst laeuft ein Anruf in einem Fenster, das es nicht gibt.
  useEffect(() => { if (blockiert) zurueckInDenTab(); }, [blockiert, zurueckInDenTab]);
  // Für den Tasten-Handler: der hängt an vielen Abhängigkeiten, und ein weiterer Neuaufbau nur
  // wegen des Modus wäre Aufwand ohne Wirkung.
  const imFensterRef = useRef(imFenster);
  imFensterRef.current = imFenster;
  const ausgelagertRef = useRef(ausgelagert);
  ausgelagertRef.current = ausgelagert;

  // Beim Aufgehen und beim Zurückholen den Fokus ins Cockpit legen: sonst müsste man erst
  // hineinklicken, bevor die Kürzel greifen — und genau das erwartet niemand direkt nach dem Wählen.
  useEffect(() => {
    if (modus === 'min') return;
    rootRef.current?.focus({ preventScroll: true });
    setAktiv(true);
  }, [modus]);

  // Nachladen, was bisher der Sales-CRM-Mount nebenbei gefüllt hat. Seit das Cockpit app-weit in
  // AppShell.tsx hängt (UMBAU.md Phase 4), kann es auch direkt von der fraud0-Karte aus aufgehen —
  // dann war SalesCrm nie gemountet und diese beiden Listen sind leer: `linkedinAccounts` trägt den
  // Absender im Skript (setterAccount, s.u.), `calls` die letzten Anrufe zu diesem Lead. Nur holen,
  // was fehlt; wer über das Sales CRM kommt, löst hier keinen einzigen Request aus.
  // (Das Skript holt sich das Cockpit ohnehin selbst, siehe getMyFork weiter unten.)
  useEffect(() => {
    if (!linkedinAccounts?.length) fetchLinkedInAccounts().catch(() => {});
    if (!calls?.length) fetchCalls().catch(() => {});
    // Absichtlich nur beim Mount: ein leerer Bestand nach dem Laden ist eine gültige Antwort und
    // darf keine Nachlade-Schleife auslösen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lokaler Anzeige-Stand des Leads · CallCockpit wird pro Call frisch gemountet
  // ({callViewOpen && <CallCockpit />} in AppShell.tsx), der Initializer greift also je Call neu.
  // Erlaubt, Name/Telefon/E-Mail im Info-Panel zu bearbeiten, ohne auf den store-weiten
  // `activeLead`-Snapshot angewiesen zu sein.
  const [liveLead, setLiveLead] = useState<Lead | null>(activeLead);

  // Info-Panel: eigener Edit-Puffer, beim Öffnen aus liveLead befüllt (siehe useEffect unten).
  const [infoName, setInfoName] = useState('');
  const [infoPhone, setInfoPhone] = useState('');
  const [infoEmail, setInfoEmail] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  // Effektives Skript (Main + eigene Fork-Blöcke) direkt laden · unabhängig davon, WIE der Call
  // gestartet wurde. So steht im Call IMMER das persönliche Skript, nie das Legacy-Markdown.
  const [fetched, setFetched] = useState<EffectiveScript | null>(null);
  const [fetchedVariantId, setFetchedVariantId] = useState<number | null>(null);
  const [blockMetaMap, setBlockMetaMap] = useState<Record<string, BlockMeta>>({});
  const variantHasSections = !!activeVariant?.section_order?.length;
  const scriptNiche = (activeLead?.niche || '').trim().toLowerCase() || '_global';
  const scriptLang = user?.lang || 'de';
  useEffect(() => {
    if (!activeLead) return;
    let cancelled = false;
    // Geholt wird IMMER — auch wenn das Skript schon woanders herkommt. Bis 2026-08-21 brach der
    // Effekt hier ab, sobald eine Variante oder ein personalisiertes Skript vorlag, und nahm die
    // block_meta mit: `objections` (welche Einwände hier Sinn machen), `next` (Antwortkette),
    // `draft` und `optional` fielen auf dem HAUPTweg still aus. Aufgefallen ist es nicht, weil
    // `kind` zusätzlich in sections_data steht — die Einteilung in Ablauf und Einwände sah also
    // richtig aus, während die Zuordnung dahinter leer blieb.
    // Die Metadaten hängen ohnehin an der Main, nicht am Fork: sie kommen aus dieser Antwort und
    // aus keiner anderen.
    const skriptKommtWoandersHer = !!personalizedScript || variantHasSections;
    salesCrmApi.getMyFork(scriptNiche, scriptLang)
      .then((res) => {
        if (cancelled) return;
        setBlockMetaMap(res.block_meta ?? {});
        if (skriptKommtWoandersHer) return;
        setFetched(res.script);
        setFetchedVariantId(res.fork?.id ?? res.main?.id ?? null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeLead, personalizedScript, variantHasSections, scriptNiche, scriptLang]);

  // --- Script-Sektionen (personalisiert > aktive Variante > selbst geladenes Fork-Skript > Roh-Script) ---
  const src = personalizedScript
    ?? (variantHasSections
      ? { sections_data: activeVariant!.sections_data, section_order: activeVariant!.section_order, hidden_sections: activeVariant!.hidden_sections ?? [] }
      : null)
    ?? fetched;
  const hasSections = !!src?.section_order?.length;
  // Ausgeblendete Blöcke werden im Call übersprungen · sie bleiben im Skript (Sales-CRM), tauchen hier
  // aber gar nicht erst auf, damit der Setter sie beim Telefonieren nicht durchblättern muss.
  // Entwürfe (block_meta.draft) sind unfertig und werden ebenfalls übersprungen.
  const hiddenSet = new Set(src?.hidden_sections ?? []);
  const order = (hasSections ? src!.section_order : ['_full'])
    .filter((k) => !hiddenSet.has(k))
    .filter((k) => !blockMetaMap[k]?.draft);
  const data = hasSections ? src!.sections_data : { _full: { title: 'Sales Script', body: salesScript || 'Kein Script hinterlegt.' } };
  const effectiveVariantId = activeVariant?.id ?? fetchedVariantId ?? undefined;

  // WEN dieses Gespräch gilt. `activeLead` ist der Sales-CRM-Lead und gehört der FIRMA — eine
  // Firma hat genau einen verlinkten CRM-Lead, also trug bis 2026-08-21 jeder Anruf den Namen der
  // Person, die dort als erste angelegt wurde. Gemessen auf Staging: Klick auf Jana Lucie Hauck,
  // Cockpit zeigte Stefan Winsel, Skript begrüßte „Hi Stefan". Der `person_key` im Protokoll war
  // die ganze Zeit richtig — es fehlte nur der Name.
  //
  // Der Wählzusammenhang bringt ihn jetzt mit (`dial_context`). Ohne ihn — Anruf aus der Lead-Liste
  // gestartet, oder die Zentrale verlangt — bleibt es beim Lead-Namen, was dort auch stimmt.
  // `activeLead` selbst wird NICHT umgeschrieben: das Info-Panel bearbeitet weiter den CRM-Satz.
  // `activeLead?.` mit Fragezeichen: an dieser Stelle ist der Lead noch nullbar — der Riegel
  // (`if (!activeLead) return null`) steht weiter unten, kurz vor dem Rendern.
  const angerufenerName = (aktuelleWahl?.person_name || '').trim()
    || liveLead?.name || activeLead?.name || '';
  const angerufenePosition = (aktuelleWahl?.person_position || '').trim() || null;

  // Auto-Fill-Kontext: [Setter] = ausgewählter LinkedIn-Account (oben rechts); fehlt der, fällt er auf
  // den eingeloggten Nutzernamen zurück (sonst stünde im Live-Skript ein leerer Platzhalter).
  // [Vorname] und [Jobtitel] kommen aus der angerufenen Person, nicht aus dem Firmen-Lead — sonst
  // stünde im gesprochenen Skript ein anderer Name als der, den man gerade am Apparat hat.
  const setterAccount = linkedinAccounts?.find((a) => a.id === selectedLinkedInAccountId);
  const fillCtx = buildScriptTokenContext(
    liveLead ?? activeLead,
    setterAccount?.name || user?.display_name || user?.username,
    { name: aktuelleWahl?.person_name ?? null, position: angerufenePosition },
  );

  // Hauptfluss (lineares Skript) vs. Abzweigungen (Einwände/Notfall · per Button erreichbar).
  // `order` ist bereits hidden-/draft-gefiltert, der Rückfall `order` selbst also ebenfalls.
  // 'note'-Blöcke (Randinfo, kein Sprechtext) tauchen in keiner der beiden Listen auf und
  // damit nie im Call.
  const mainOrder = order.filter((k) => kindOf(k, data[k], blockMetaMap[k]) === 'flow');
  const branchKeys = order.filter((k) => kindOf(k, data[k], blockMetaMap[k]) === 'objection');
  // Hauptfluss + synthetische Buchungs-Sektion am Ende (Calendly).
  const flow = [...(mainOrder.length ? mainOrder : order), BOOKING_KEY];
  const total = flow.length;

  // mainIdx = Position im Hauptfluss; branchKey != null → gerade in einer Abzweigung (Detour).
  const [mainIdx, setMainIdx] = useState(0);
  const [branchKey, setBranchKey] = useState<string | null>(null);
  // Das Gedächtnis des Gesprächs: welcher Absatz stand auf dem Schirm, und wie oft.
  //
  // Bis zum 2026-08-20 wurde das NIRGENDS mitgeschrieben. Der Anruf schickte einen Schnappschuss
  // des ganzen Skripts mit, aber nicht, was davon benutzt wurde — also stand die Erfolgsquote je
  // Baustein ohne Datenlieferant da, obwohl das Erfolgs-Buch das Feld dafür hat.
  //
  // ZÄHLER, NICHT LISTE: „wie oft" ist die Frage hinter den Einwand-Stufen (derselbe Einwand zum
  // zweiten Mal → Stufe 2). Eine Menge besuchter Schlüssel könnte sie nicht beantworten, und man
  // merkte es erst, wenn die Stufen gebaut sind.
  //
  // Er lebt im Browser und nicht auf dem Server: ein abgebrochenes Gespräch meldet nichts, und
  // auf eine Serverantwort zu warten, während jemand telefoniert, wäre die falsche Reihenfolge.
  const [besuchteAbsaetze, setBesuchteAbsaetze] = useState<Record<string, number>>({});
  // Welche Stufe der Abzweigung gerade dasteht (ARA → ACE). Bei JEDEM Wechsel der Abzweigung
  // zurück auf null: ein neuer Einwand fängt bei seiner ersten Stufe an, nie mittendrin.
  const [teilIdx, setTeilIdx] = useState(0);
  const zurAbzweigung = useCallback((k: string | null) => { setBranchKey(k); setTeilIdx(0); }, []);
  const [elapsed, setElapsed] = useState(0);
  // Aufnahme-Anzeige (roter Punkt): startet schon vor diesem Mount (useTrackedDial), hier nur
  // reaktiv ausgelesen. Kein eigener Timer nötig — `elapsed` (Call-Timer, s.u.) läuft praktisch
  // im selben Fenster.
  const { isRecording } = useCallRecordingState();
  const [outcome, setOutcome] = useState<CallOutcome | null>(null);
  const [emotions, setEmotions] = useState<EmotionTag[]>([]);
  const [notes, setNotes] = useState('');
  // „Ich schicke Ihnen was" ist der häufigste Ausgang eines guten Gesprächs — und war bisher an
  // keiner Stelle festgehalten, an der man später danach fragen könnte. Der Vermerk fällt deshalb
  // hier ab, im Abschluss, wo der Setter ohnehin steht. Zwei Tage später fragt die Telefon-Tabelle
  // von selbst nach der Antwort.
  const [mailGeschickt, setMailGeschickt] = useState(false);
  // „Schicken Sie mir mal was" ist der häufigste Ausgang eines guten Gesprächs — bis hierher konnte
  // man ihn nur ANKREUZEN, geschrieben wurde die Mail dann irgendwo anders (oder gar nicht). Das
  // Mail-Pult der fraud0-Karte kann das längst: Vorlagen, Sie/Du, Betreff, Kopieren, Verschickt-
  // Stempel. Es steht hier deshalb als DASSELBE Bauteil im Abschluss, nicht als zweiter Composer —
  // ein zweiter hätte eigene Vorlagen, und die zwei Bestände wären am zweiten Tag auseinander.
  //
  // Es braucht den fraud0-Lead (der Vorlagentext lebt von dessen Zahlen). Ohne angeklickte Nummer
  // gibt es den Bezug nicht — dann bleibt es beim bloßen Vermerk, wie bisher.
  const [mailLead, setMailLead] = useState<DomainLead | null>(null);
  // Aus dem Mail-Fenster wieder DORTHIN zurück, wo man es geöffnet hat: aus dem Gespräch zurück ins
  // Skript, aus dem Abschluss zurück in den Abschluss. Sonst stünde man nach dem Schreiben im
  // falschen Bild und müsste den Weg neu suchen.
  // Wohin das Mail-Pult zurueckfuehrt. 'ende' ist der Auffang-Fall aus `save`: dort ist der Anruf
  // BEREITS gespeichert, ein Rueckweg ins Gespraech waere eine Falle — ein zweiter Druck auf
  // „Call speichern" legte einen zweiten Anruf an.
  const [mailZurueck, setMailZurueck] = useState<'call' | 'save' | 'ende'>('save');
  // An welchen fraud0-Lead die Mail geht. ZWEI Quellen, und die zweite hat gefehlt:
  //   1. der Wählzusammenhang (Nummer angeklickt) — er nennt zusätzlich die PERSON,
  //   2. `fraud0_lead_id` am Sales-CRM-Lead, die Rückverknüpfung aus der Übergabe.
  // Bis 2026-08-15 hing der Mail-Knopf allein an (1). Wer den Anruf aus der Lead-Liste startete
  // (`startCall` ohne Wahl, SalesCrm.tsx), sah ihn deshalb grau — obwohl der Bezug über (2) längst
  // bekannt war. Geraten wird weiterhin nichts: fehlen beide, bleibt der Knopf aus.
  const mailLeadId = aktuelleWahl?.lead_id
    ?? liveLead?.fraud0_lead_id ?? activeLead?.fraud0_lead_id ?? null;
  const [mailFehler, setMailFehler] = useState(false);
  // Das Speichern ist fehlgeschlagen. Sichtbar im Abschluss-Panel, damit ein Fehlschlag nicht
  // dasselbe aussieht wie ein Erfolg (siehe `save`).
  const [speicherFehler, setSpeicherFehler] = useState(false);
  // Hat das Pult den Vermerk schon geschrieben, darf `save()` ihn nicht ein zweites Mal schreiben —
  // sonst stünden zwei Mails im Verlauf, von denen es nur eine gibt.
  const [mailVermerkt, setMailVermerkt] = useState(false);
  // „Wann wieder anrufen" — geteilte Zeitwahl, siehe components/CallbackPicker.tsx.
  const {
    presets, presetIdx, callbackAt, exactOpen,
    setzeVorwahl, waehlePreset, waehleGenau, toggleExact, merkeAlsVorschlag, aktuellerIso,
  } = useCallbackPresets();
  const [mode, setMode] = useState<'call' | 'notes' | 'save' | 'confirm' | 'shortcall' | 'info' | 'mail'>('call');
  const [saving, setSaving] = useState(false);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  // ── Welche Nummer ist gemeint? ───────────────────────────────────────────────────────────────
  // Ein Anschluss-Befund („Nummer falsch", „nicht vergeben") traf bisher IMMER genau die eine
  // gewählte Nummer. Bei einem Kontakt mit mehreren Anschlüssen ist das oft nicht die Aussage, die
  // am Telefon gefallen ist: wer die Zentrale anruft und hört „die Durchwahl gibt es nicht mehr",
  // meint eine ANDERE Nummer als die gewählte. Ohne Wahlmöglichkeit landet dieser Befund wieder in
  // der Notiz — wo ihn der nächste Anruf nicht sieht und die falsche Nummer wählbar bleibt.
  //
  // Deshalb steht die Auswahl im Abschluss, aber nur dort, wo sie eine Frage beantwortet: hat der
  // Kontakt genau eine Nummer, bleibt alles wie bisher. Vorgewählt ist immer die gewählte Nummer,
  // der Normalfall kostet also keinen Klick.
  const [kontaktNummern, setKontaktNummern] = useState<LeadPhone[]>([]);
  const [defektKeys, setDefektKeys] = useState<string[]>([]);

  // Erst im Abschluss laden: währenddessen wird telefoniert, und vorher weiss niemand, ob überhaupt
  // ein Anschluss-Befund kommt. Nur die Nummern DESSELBEN Kontakts — eine fremde Personenzeile zu
  // sperren wäre eine Aussage über ein Gespräch, das mit ihr nicht stattgefunden hat.
  useEffect(() => {
    const wahl = aktuelleWahl;
    if (mode !== 'save' || !wahl?.lead_id || !wahl.person_key) return;
    let verworfen = false;
    leadGenApi.phones(wahl.lead_id)
      .then((res) => {
        if (!verworfen) {
          setKontaktNummern((res.phones ?? []).filter((p) => p.person_key === wahl.person_key));
        }
      })
      .catch(() => { /* ohne Liste bleibt es bei der gewählten Nummer — genau wie bisher */ });
    return () => { verworfen = true; };
  }, [mode, aktuelleWahl]);

  // Vorwahl = die tatsächlich gewählte Nummer. Ohne Wählzusammenhang bleibt sie leer, und dann wird
  // — unverändert — nichts gesperrt: welche Nummer gemeint wäre, ist dann nicht bekannt. Die Liste
  // fällt mit: sie gehört zum Kontakt, den man gerade angerufen hat, nicht zum vorigen.
  useEffect(() => {
    setDefektKeys(aktuelleWahl?.phone_key ? [aktuelleWahl.phone_key] : []);
    setKontaktNummern([]);
  }, [aktuelleWahl?.lead_id, aktuelleWahl?.phone_key]);

  const toggleDefektKey = useCallback((key: string) => {
    setDefektKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  // Für die Erklärzeile: die ausgewählten Nummern im Klartext. Ist die Liste (noch) nicht da, steht
  // dort wie bisher die gewählte Nummer — die Aussage stimmt in beiden Fällen.
  const defektZiele = kontaktNummern.filter((p) => defektKeys.includes(p.phone_key));
  const defektNamen = (defektZiele.length ? defektZiele.map((p) => p.phone) : [aktuelleWahl?.phone])
    .filter(Boolean).join(' · ');
  const mehrereZiele = defektZiele.length > 1;
  // Die Wahl steht nur da, wo sie eine Frage beantwortet: mehr als ein Anschluss an diesem Kontakt,
  // und ein Befund, der überhaupt greift (an der Zentrale sperrt „nicht zuständig" nichts).
  const zeigeNummernwahl = !!outcome && !!defektFuer(outcome, aktuelleWahl?.person_key)
    && kontaktNummern.length > 1;

  const idx = Math.min(mainIdx, Math.max(0, total - 1)); // geklemmt (Skript kann async nachladen)
  const onBranch = branchKey != null && !!data[branchKey];
  const curKey = onBranch ? branchKey! : flow[idx];
  const isBooking = !onBranch && curKey === BOOKING_KEY;
  // Zählt jeden Wechsel auf einen anderen Absatz — egal über welchen Weg (Knopf, Pfeiltaste,
  // Ziffer, Einwand). Deshalb hier an `curKey` und nicht an jeder einzelnen Navigation: die gibt
  // es an sechs Stellen, und die siebte vergäße man.
  //
  // Der Ref verhindert zweierlei: dass ein erneutes Rendern denselben Absatz nochmal zählt, und
  // dass React im Entwicklungsmodus (StrictMode ruft Effekte doppelt) jede Zahl verdoppelt.
  // A → B → A zählt A trotzdem zweimal, und das ist genau richtig: das IST ein zweiter Besuch.
  const zuletztGezaehlt = useRef<string | null>(null);
  useEffect(() => {
    if (!curKey || zuletztGezaehlt.current === curKey) return;
    zuletztGezaehlt.current = curKey;
    setBesuchteAbsaetze((v) => ({ ...v, [curKey]: (v[curKey] ?? 0) + 1 }));
  }, [curKey]);
  const cur = data[curKey] ?? { title: '', body: '' };
  const curBody = fillPlaceholders(cur.body || '', fillCtx); // Platzhalter live ersetzt
  // Die Stufen des Blocks (@@ ARA @@ / @@ ACE @@). Ausserhalb einer Abzweigung uninteressant —
  // Ablauf-Blöcke tragen keine Marken, dort ist es immer genau eine Stufe.
  const teile = skriptTeile(curBody);
  const teilNr = Math.min(teilIdx, teile.length - 1);
  const aktuellerTeil = teile[teilNr];
  const naechsterTeil = teile[teilNr + 1] ?? null;
  const nextTitle = !onBranch && idx < total - 1
    ? (flow[idx + 1] === BOOKING_KEY ? 'Termin buchen' : (data[flow[idx + 1]]?.title ?? ''))
    : '';
  // Nur die Einwände, die an der aktuellen Sektion Sinn machen (Position = flow[idx], auch aus einer
  // Abzweigung heraus, damit man von dort zu einem anderen passenden Einwand springen kann).
  // Welche Abzweigungen an DIESER Stelle angeboten werden.
  //
  // In einer Sektion: die Einwände, die zur Position passen (block_meta.objections der Bibliothek,
  // sonst der Rückfall in objectionsForSection).
  //
  // In einer Abzweigung: die, die als NÄCHSTES Sinn machen — ein Einwand ist kein Register, das
  // man aufschlägt, sondern eine Stelle im Gespräch mit wenigen plausiblen Fortsetzungen.
  //   1. Steht im Block eine eigene Liste (block_meta[key].objections), gilt genau sie. Das ist
  //      der vorgesehene Weg; heute ist die Liste im Bestand überall leer.
  //   2. Sonst greift EINE Regel, und zwar die einzige, die sich aus dem Gesprächsverlauf ableiten
  //      lässt statt aus einer Vermutung über einzelne Paare: aus einem EINWAND heraus fallen die
  //      ABLEHNUNGEN weg. „Woher hast du meine Nummer" ist der Reflex auf den Opener — wer schon
  //      über die Agentur redet, mit der er zusammenarbeitet, sagt das nicht mehr. Umgekehrt gilt
  //      das nicht: aus einer Ablehnung führt der Weg sehr wohl in die nächste („kein Interesse"
  //      → „ruf mich später an"), dort bleibt also alles stehen.
  // Der aktive Einwand selbst bleibt in jedem Fall in der Liste — man muss sehen, wo man steht.
  const angeboteneBranches = (() => {
    if (isBooking) return [];
    const ausSektion = objectionsForSection(flow[idx], branchKeys, blockMetaMap);
    if (!onBranch || !branchKey) return ausSektion;
    const ausBlock = (blockMetaMap[branchKey]?.objections ?? []).filter((k) => branchKeys.includes(k));
    if (ausBlock.length) {
      const erlaubt = new Set([branchKey, ...ausBlock]);
      return ausSektion.filter((k) => erlaubt.has(k));
    }
    if (istAblehnung(data[branchKey]?.title || branchKey)) return ausSektion;
    return ausSektion.filter((k) => k === branchKey || !istAblehnung(data[k]?.title || k));
  })();
  // Nach Rubrik gebündelt — und in dieser Ordnung bedient, damit die Taste auf der Karte dieselbe
  // ist, die sie auslöst.
  const branchGruppen = gruppiereBranches(angeboteneBranches, (k) => data[k]?.title || k);
  const visibleBranches = branchGruppen.flatMap((g) => g.keys);
  // Taste → Abzweigung. Zwei getrennte Reihen (s. TASTEN_ABLEHNUNG/TASTEN_EINWAND), jede für sich
  // durchgezählt: so verschiebt eine Ablehnung mehr nicht die Ziffern der Einwände.
  const tasteVon = new Map<string, string>();   // branchKey → Taste
  const branchVonTaste = new Map<string, string>();
  {
    let a = 0, e = 0;
    for (const k of visibleBranches) {
      const ablehnung = istAblehnung(data[k]?.title || k);
      const taste = ablehnung ? TASTEN_ABLEHNUNG[a++] : TASTEN_EINWAND[e++];
      if (!taste) continue;                     // Reihe voll — der Rest bleibt per Klick erreichbar
      tasteVon.set(k, taste);
      branchVonTaste.set(taste, k);
    }
  }
  // Nächste Stufe einer Antwortkette („kein Interesse" Stufe 1 → 2 → 3). Nur gültig, solange der
  // Folgeblock im Skript auch wirklich vorhanden und nicht ausgeblendet/Entwurf ist — sonst führte
  // der Knopf auf einen leeren Bildschirm.
  const nextStageRaw = onBranch ? blockMetaMap[branchKey!]?.next : null;
  const nextStageKey = nextStageRaw && branchKeys.includes(nextStageRaw) ? nextStageRaw : null;
  // Vorzimmer-Schritte darf der Setter überspringen: wer direkt beim Ansprechpartner landet, hat
  // sie nie gebraucht. Ziel ist der erste Schritt, der nicht als optional markiert ist.
  const firstRequiredIdx = flow.findIndex((k) => !blockMetaMap[k]?.optional);
  const canSkipOptional = !onBranch && !isBooking
    && !!blockMetaMap[curKey]?.optional && firstRequiredIdx > idx;

  // Volltext-Modus: ganzes Skript am Stück statt einer Sektion · plus weiches Dauerscrollen per Pfeiltaste
  // (rAF-Loop statt Sprüngen, damit man beim Vorlesen mitkommt).
  const [fullText, setFullText] = useState(false);

  // --- Lesbarkeit: Helligkeit und Schriftgrad, beides im Cockpit selbst und beides gemerkt ---
  //
  // Eigene Schalter statt der App-Einstellung: das Cockpit ist die einzige Ansicht, aus der zwei
  // Meter Abstand vorgelesen wird, und es steht mal im dunklen Büro und mal am hellen Fenster,
  // während die Telefon-Tabelle daneben unangetastet bleibt.
  const [callDark, setCallDark] = useState(() =>
    typeof localStorage === 'undefined' || localStorage.getItem('call_theme') !== 'light');
  const [schriftIdx, setSchriftIdx] = useState(() => {
    const n = Number(localStorage.getItem('call_font'));
    return Number.isInteger(n) && n >= 0 && n < SCHRIFT_STUFEN.length ? n : SCHRIFT_STANDARD;
  });
  useEffect(() => { localStorage.setItem('call_theme', callDark ? 'dark' : 'light'); }, [callDark]);
  useEffect(() => { localStorage.setItem('call_font', String(schriftIdx)); }, [schriftIdx]);
  // Klemmen statt umlaufen: am Anschlag soll nichts passieren, nicht von riesig auf winzig springen.
  const groesser = useCallback(() => setSchriftIdx((i) => Math.min(i + 1, SCHRIFT_STUFEN.length - 1)), []);
  const kleiner  = useCallback(() => setSchriftIdx((i) => Math.max(i - 1, 0)), []);
  // Grundgrade in rem, mal Stufe. Zwei Staffeln, weil Fenster und Vollbild verschiedene Flächen
  // sind: die Vollbildgrade sind fürs Vorlesen aus zwei Metern gemacht und würden im Fenster nach
  // drei Wörtern umbrechen. Der Regler verschiebt beide, er ersetzt sie nicht.
  const skala = SCHRIFT_STUFEN[schriftIdx];
  const gradTitel = `${(imFenster ? 1.5 : 3.0) * skala}rem`;
  const gradText = `${(imFenster ? 1.05 : 1.5) * skala}rem`;
  const amMinimum = schriftIdx === 0;
  const amMaximum = schriftIdx === SCHRIFT_STUFEN.length - 1;

  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const velRef = useRef(0);
  const stopScroll = useCallback(() => {
    velRef.current = 0;
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);
  const startScroll = useCallback((v: number) => {
    velRef.current = v;
    if (rafRef.current != null) return;  // Loop läuft schon · nur Richtung/Tempo aktualisiert
    const step = () => {
      const el = scrollRef.current;
      if (!el || velRef.current === 0) { rafRef.current = null; return; }
      el.scrollTop += velRef.current;    // scrollTop klemmt sich selbst an 0/max → kein Überschiessen
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);
  const initials = (angerufenerName || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';

  // Navigation: weiter/zurück im Hauptfluss; aus einer Abzweigung führt jede Navigation zurück zum
  // Hauptfluss (Detour verlassen, Position bleibt erhalten → „weiter" setzt das Skript fort).
  // Die konkreten Übergänge stehen inline im Keyboard-Handler und an den Buttons (setBranchKey/setMainIdx).

  // Im Cockpit ist der Standard-Vorschlag vorgewählt, damit „Niemand rangegangen" ein einziger
  // Tastendruck bleibt. Steht die Liste noch aus, greift die Vorwahl beim Eintreffen.
  useEffect(() => { setzeVorwahl(true); }, [setzeVorwahl]);

  // --- Live-Timer ---
  useEffect(() => {
    if (!callTimerStart) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - callTimerStart) / 1000)), 1000);
    return () => clearInterval(t);
  }, [callTimerStart]);

  const toggleEmotion = useCallback((e: EmotionTag) => {
    setEmotions((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  }, []);

  const save = useCallback(async (outcomeOverride?: CallOutcome) => {
    if (!activeLead || saving) return;
    // Guard: bei onClick={save} kommt ein Event rein · nur echte Outcome-Strings zählen als Override.
    const effOutcome = (typeof outcomeOverride === 'string' ? outcomeOverride : null) ?? outcome;
    setSaving(true);
    setSpeicherFehler(false);
    // Bleibt das Pult offen? Zwei Gruende: das Speichern ist fehlgeschlagen, oder das Mail-Fenster
    // hat niemanden gefunden (beides weiter unten). In beiden Faellen waere Zuklappen ein stiller
    // Verlust — es saehe genauso aus wie ein geglueckter Abschluss.
    let offenLassen = false;
    try {
      // Lokaler Zeitstempel ('YYYY-MM-DD HH:MM:SS') → Wochentag/Uhrzeit-Analytik nutzt die Caller-Zeitzone.
      const now = new Date();
      const p = (n: number) => String(n).padStart(2, '0');
      const callTime = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
      // Bei WON/LOST ist der Picker verborgen — der vorgewählte Vorschlag steht aber trotzdem im
      // Zustand und würde sonst einen Rückruf auf einen abgeschlossenen Lead stempeln. Dasselbe
      // gilt für die beiden Anschluss-Befunde: eine tote Nummer bekommt KEINE Wiedervorlage, sonst
      // käme derselbe Anschluss in drei Tagen wieder hoch, den man gerade als nicht vergeben
      // erkannt hat — und die Liste wäre wieder voll mit Arbeit, die niemand tun kann.
      // Ohne gewähltes Ergebnis bleibt die Zeit erhalten (Lücke, keine Aussage).
      const effCallback = (effOutcome && !CALL_OUTCOME_META[effOutcome].wiedervorlage)
        ? undefined : (aktuellerIso() ?? undefined);
      const suggested_ = await addCall({
        lead_id: activeLead.id,
        length_seconds: elapsed || undefined,
        outcome: effOutcome ?? undefined,
        emotions,
        notes: notes || undefined,
        call_time: callTime,
        callback_at: effCallback,
        script_variant_id: effectiveVariantId,
        call_script_sections: hasSections ? src!.sections_data : undefined,
        call_script_section_order: hasSections ? src!.section_order : undefined,
        // Was tatsächlich auf dem Schirm stand — der Schnappschuss darüber sagt nur, was zur
        // Auswahl gestanden hätte. Ohne dieses Feld bleibt „je Baustein" für immer leer.
        benutzte_absaetze: Object.keys(besuchteAbsaetze),
        personalization_context: personalizedScript ? { personalized: true } : undefined,
        has_recording: isRecording,
      });
      const { suggestedStatus: suggested, callId } = suggested_;
      // Aufnahme stoppen + hochladen — NACH dem Speichern, weil erst dann die call_id existiert,
      // an die die Aufnahme gehängt wird. Läuft bewusst fire-and-forget (kein await davor, das den
      // Cockpit-Schluss verzögert): ein fehlgeschlagener Upload darf den gespeicherten Anruf nicht
      // in der Schwebe halten.
      stopCallRecording().then((blob) => {
        if (blob && callId) {
          salesCrmApi.uploadCallRecording(callId, blob).catch(() => {
            // Der Call ist gespeichert — ein fehlgeschlagener Aufnahme-Upload darf ihn nicht kippen.
            // #call-review bekommt in dem Fall trotzdem einen Post (server-seitiger Fallback bei
            // fehlendem "has_recording"-Eintreffen greift hier NICHT, deshalb bewusst kein Retry —
            // die Aufnahme selbst ist einfach weg, akzeptiertes Risiko bei einem Netzfehler genau
            // in diesem kleinen Fenster).
          });
        }
      });
      // Der Befund gehört an die NUMMER, nicht nur ans Gespräch: nur so verschwindet der Wähl-Knopf
      // an diesem Anschluss (harte Sperre, `PhoneList`/`PhoneCell`) und die nächste Kollegin wählt
      // ihn nicht nochmal. Genau dafür gibt es `set_phone_defect` schon — bisher aber nur über das
      // Stift-Menü an der Nummer, also als eigener Arbeitsschritt, den nach einem Anruf niemand
      // geht. Deshalb standen die Befunde bis heute in Notizen („falsche nummer", „Nummer nicht
      // vergeben") statt an der Nummer selbst. Jetzt fällt der Vermerk als Nebenprodukt der
      // Disposition ab.
      //
      // Fehlt der Wählzusammenhang (Cockpit aus der Lead-Liste geöffnet, kein Anschluss geklickt),
      // wird NICHTS markiert — welche der Nummern gemeint wäre, ist dann nicht bekannt, und raten
      // wäre hier schlimmer als nichts zu tun. Das Ergebnis am Gespräch steht trotzdem.
      //
      // `defektKeys` ist mit der gewählten Nummer vorbelegt und enthält bei einem Kontakt mit
      // mehreren Anschlüssen das, was der Setter oben ausgewählt hat — nacheinander, damit ein
      // fehlgeschlagener Vermerk die übrigen nicht mitreisst.
      const defekt = defektFuer(effOutcome, aktuelleWahl?.person_key);
      if (defekt && aktuelleWahl?.lead_id) {
        for (const phoneKey of defektKeys) {
          try {
            await leadGenApi.setPhoneDefect(aktuelleWahl.lead_id, {
              personKey: aktuelleWahl.person_key,
              central: aktuelleWahl.person_key === CENTRAL_PERSON_KEY,
              phoneKey,
              reason: defekt,
              note: DEFEKT_NOTIZ[defekt],
            });
          } catch { /* Der Anruf ist gespeichert — ein fehlgeschlagener Vermerk darf ihn nicht kippen */ }
        }
      }
      // Ist der Lead bekannt (Wählzusammenhang ODER `fraud0_lead_id`), wird vermerkt; ist er es
      // nicht, passiert nichts — geraten wird auch hier nichts. Der `person_key` reist nur aus dem
      // Wählzusammenhang mit: nur DER sagt, mit wem gesprochen wurde. Ohne ihn gilt die Mail dem
      // Haus, und die Telefon-Tabelle zeigt sie in jeder Zeile der Firma.
      if (mailGeschickt && !mailVermerkt && mailLeadId) {
        try {
          await leadGenApi.markMailSent(mailLeadId, true, undefined, undefined,
                                        undefined, undefined, undefined,
                                        aktuelleWahl?.person_key ?? undefined);
        } catch { /* siehe oben — der Anruf steht, der Vermerk ist die Zugabe */ }
      }
      const patch: Partial<Lead> = {};
      if (suggested) patch.status = suggested as LeadStatus;
      // follow_up_date wird NICHT mehr von hier gesetzt: der Server schreibt es aus callback_at,
      // zusammen mit domain_leads.callback_at. Zwei Schreiber auf einer Spalte wären ein verlorenes
      // Update gegen die fraud0-Brücke.
      if (Object.keys(patch).length) {
        await updateLead(activeLead.id, patch);
      }
      // „Mail gewünscht" ist das einzige Ergebnis, das einen NÄCHSTEN Handgriff meint statt eines
      // Abschlusses. Deshalb öffnet es das Mail-Fenster — dasselbe Verhalten, das die Anruf-Werkbank
      // im Telefon-Stapel hatte (CallWorkbench.tsx: dispatch 'fraud0-mail-requested'). Gehört wird
      // es von `EmailOutreach` auf der Karte UND vom Mail-Fenster der Anrufliste.
      //
      // Nach dem Speichern und ausserhalb des try/catch-Ergebnisses: das Fenster darf aufgehen,
      // auch wenn ein Defekt-Vermerk oder der Mail-Stempel darüber fehlgeschlagen ist.
      if (effOutcome === 'MAIL GEWÜNSCHT') {
        const mailId = aktuelleWahl?.lead_id ?? mailLeadId ?? activeLead.fraud0_lead_id ?? null;
        if (mailId) {
          // `handled` ist die Empfangsbestaetigung. Beide Hoerer laufen SYNCHRON in
          // `dispatchEvent`, die Antwort steht in der naechsten Zeile also schon fest.
          const detail: MailWunschDetail = {
            leadId: mailId, personKey: aktuelleWahl?.person_key ?? null, handled: false,
          };
          window.dispatchEvent(new CustomEvent('fraud0-mail-requested', { detail }));
          // Niemand da — der Anruf kam aus der Lead-Liste, weder fraud0-Karte noch Anrufliste sind
          // montiert. Bis zum 2026-08-20 passierte hier NICHTS: kein Fenster, keine Meldung. Wer
          // „Mail gewuenscht" gewaehlt hatte, stand vor einer geschlossenen Tuer und hielt es fuer
          // den eigenen Fehlklick. Das Cockpit hat sein eigenes Mail-Pult (mode 'mail', gleiche
          // `mailLeadId`) — es aufzumachen ist der ehrliche Ausweg, nicht ein Hinweis auf einen
          // Ort, den man erst suchen muesste.
          if (!detail.handled) {
            setMailZurueck('ende');
            setMode('mail');
            offenLassen = true;
          }
        }
      }
    } catch {
      // Bis zum 2026-08-20 stand hier KEIN catch. Schlug `addCall` fehl (Netz weg, 400, 500),
      // sprang der Ablauf direkt ins finally: das Cockpit schloss sich wie nach einem geglueckten
      // Abschluss, der Anruf war aber nicht gespeichert und die Notizen weg. Gemerkt hat man es
      // erst, wenn die Firma spaeter in keiner Nachfass-Liste auftauchte.
      //
      // Die beiden Zugaben darueber (Defekt-Vermerk, Mail-Stempel) haben weiterhin ihr eigenes
      // catch und duerfen den Anruf NICHT kippen — nur das Speichern des Anrufs selbst landet hier.
      setSpeicherFehler(true);
      offenLassen = true;
    } finally {
      setSaving(false);
      // Nur zumachen, wenn es etwas zuzumachen gibt: ein Fehlschlag oder ein ungehoertes
      // Mail-Ereignis braucht das Pult noch.
      if (!offenLassen) endCall();
    }
  }, [activeLead, saving, addCall, elapsed, outcome, emotions, notes, aktuellerIso, effectiveVariantId, hasSections, src, personalizedScript, updateLead, endCall, aktuelleWahl, mailGeschickt, mailVermerkt, mailLeadId, defektKeys, isRecording]);

  // Das Mail-Pult zumachen. Nach dem Auffang-Fall ist das Gespraech erledigt, sonst geht es dorthin
  // zurueck, wo es aufgerufen wurde.
  const mailPultSchliessen = useCallback(() => {
    if (mailZurueck === 'ende') endCall();
    else setMode(mailZurueck);
  }, [mailZurueck, endCall]);

  // Den fraud0-Lead erst holen, wenn das Mail-Pult aufgeht: es ist der seltene Weg, und ein Abruf
  // je Anruf wäre ein Request für etwas, das die meisten Gespräche nie brauchen.
  useEffect(() => {
    if (mode !== 'mail' || mailLead || !mailLeadId) return;
    let abgebrochen = false;
    setMailFehler(false);
    leadGenApi.getLead(mailLeadId)
      .then((l) => { if (!abgebrochen) setMailLead(l); })
      .catch(() => { if (!abgebrochen) setMailFehler(true); });
    return () => { abgebrochen = true; };
  }, [mode, mailLead, mailLeadId]);

  // Abschluss öffnen: bei sehr kurzem Call erst fragen, ob überhaupt getrackt werden soll.
  const goToSave = useCallback(() => {
    setMode(elapsed < SHORT_CALL_SECONDS ? 'shortcall' : 'save');
  }, [elapsed]);
  // „Nicht rangegangen" · sofort als Nicht-erreicht speichern und beenden.
  const quickNoAnswer = useCallback(() => { save('NICHT ERREICHT' as CallOutcome); }, [save]);
  // „Nummer nicht vergeben" · genauso ein Ausgang, der KEINEN Abschluss braucht: es gibt nichts zu
  // notieren, keine Emotion, keine Wiedervorlage (`wiedervorlage: false`). Wer die Ansage „kein
  // Anschluss unter dieser Nummer" hört, legt nach fünf Sekunden auf — der Weg über „Als Call
  // tracken" und die Ergebnis-Knöpfe war für diesen Fall ein Umweg, den niemand geht. Der
  // Defekt-Vermerk fällt in `save()` genauso ab wie aus dem Abschluss-Panel (`defektKeys` ist mit
  // der gewählten Nummer vorbelegt); für eine ANDERE Nummer des Kontakts führt der Weg weiterhin
  // über den Abschluss, wo die Nummernwahl steht.
  const quickNummerNichtVergeben = useCallback(() => { save('NUMMER NICHT VERGEBEN' as CallOutcome); }, [save]);

  // „Verwerfen (nicht tracken)" — und zwar wörtlich: der Wählklick hat den Versuch bereits
  // protokolliert (`log_dial`, derselbe Weg für Stapel und Telefon-Tabelle), und bis 2026-08-18
  // blieb der stehen. Die Person galt danach als angerufen und rutschte in der Telefon-Tabelle aus
  // „nie angerufen" ans Ende der Liste — wer sich verwählt oder das Cockpit versehentlich geöffnet
  // hatte, suchte seine Zeile unten wieder. Der Versuch wird deshalb zurückgenommen.
  //
  // Ohne Wählzusammenhang (`aktuelleWahl` ist null, wenn der Anruf aus der Lead-Liste gestartet
  // wurde) gibt es nichts zurückzunehmen — dann bleibt es beim reinen Schliessen.
  //
  // GEWARTET wird auf die Antwort, bevor das Cockpit zugeht — obwohl das eine halbe Sekunde kostet.
  // Der Grund ist genau der Fehler, den diese Änderung behebt: die Telefon-Tabelle steht still,
  // solange ein Gespräch läuft, und zieht in DEM Moment nach, in dem das Cockpit schliesst. Ginge
  // die Rücknahme nebenher, gewänne dieses Nachziehen das Rennen — die Zeile rutschte sichtbar nach
  // unten und käme eine Viertelsekunde später wieder hoch. Ein Zappeln, das aussieht wie ein Fehler.
  //
  // Die Wettlauf-Bremse daneben ist das Sicherheitsnetz: hängt das Netz, geht das Cockpit trotzdem
  // zu. Ein Anruf-Fenster, das sich nicht schliessen lässt, wäre der teurere Fehler.
  const verwirftGerade = useRef(false);
  const verwerfen = useCallback(async () => {
    // Zweimal geklickt (oder ↵ gehalten) darf NICHT zweimal löschen: der zweite Aufruf träfe den
    // nächstälteren eigenen Wählversuch auf demselben Lead — einen, den niemand verwerfen wollte.
    if (verwirftGerade.current) return;
    verwirftGerade.current = true;
    const wahl = aktuelleWahl;
    if (wahl?.lead_id) {
      await Promise.race([
        leadGenApi.discardDial(wahl.lead_id, wahl.phone, wahl.person_key)
          .catch(() => { /* stiller Fehlschlag: dann bleibt der Versuch im Verlauf stehen */ }),
        new Promise((fertig) => setTimeout(fertig, 1500)),
      ]);
    }
    endCall();
  }, [aktuelleWahl, endCall]);

  // Info-Panel öffnet mit dem aktuellen Kontaktstand · frischer Puffer bei jedem Öffnen.
  useEffect(() => {
    if (mode !== 'info') return;
    setInfoName(liveLead?.name ?? '');
    setInfoPhone(liveLead?.phone ?? '');
    setInfoEmail(liveLead?.email ?? '');
    setInfoError(null);
  }, [mode, liveLead]);

  const saveInfo = useCallback(async () => {
    if (!activeLead) return;
    const patch: Partial<Lead> = {};
    const trimmedName = infoName.trim();
    if (trimmedName && trimmedName !== liveLead?.name) patch.name = trimmedName;
    if (infoPhone !== (liveLead?.phone ?? '')) patch.phone = infoPhone;
    if (infoEmail !== (liveLead?.email ?? '')) patch.email = infoEmail;
    if (!Object.keys(patch).length) { setMode('call'); return; }
    setSavingInfo(true);
    setInfoError(null);
    try {
      await updateLead(activeLead.id, patch);
      setLiveLead((prev) => (prev ? { ...prev, ...patch } : prev));
      setMode('call');
    } catch {
      setInfoError('Speichern fehlgeschlagen · nochmal versuchen.');
    } finally {
      setSavingInfo(false);
    }
  }, [activeLead, infoName, infoPhone, infoEmail, liveLead, updateLead]);

  // --- Keyboard-Steuerung ---
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      const typing = tag === 'TEXTAREA' || tag === 'INPUT';

      // Im Fenster-Modus gehören die Kürzel dem Cockpit nur, solange der Fokus darin liegt —
      // sonst beendete „X" einen Anruf, während man nebenan in der Telefon-Tabelle blättert.
      // Absichtlich gegen das DOM geprüft statt gegen einen Zustand: `document.activeElement` ist
      // immer aktuell, ein gespiegelter Zustand kann veralten.
      // Nur im Tab-Fenster noetig: dort liegt unter dem Cockpit die Seite mit eigenen
      // Tastenhandlern. Ein ausgelagertes Fenster hat nichts darunter — dort gehoeren alle
      // Tasten ihm, und ein Fokus-Tor spraeche ihm seine eigenen Kuerzel ab.
      if (!ausgelagertRef.current && imFensterRef.current
          && !rootRef.current?.contains(document.activeElement)) return;

      // Die Taste gehört dem Cockpit → sie darf die Seite DARUNTER nicht mehr erreichen. Begründung
      // und Grenzen der Liste stehen in `lib/callKeys.ts`, geprüft von dessen Tests.
      if (gehoertDemCockpit(e.key, typing)) e.stopPropagation();

      if (mode === 'notes' || mode === 'info') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); }
        return; // Tippen fliesst in die Felder
      }

      // Das Mail-Pult kam aus dem Abschluss — Esc führt dorthin zurück, nicht ins Skript. Sonst
      // stünde man nach dem Schreiben wieder im Gespräch und müsste den Abschluss neu suchen.
      if (mode === 'mail') {
        // Zurück dorthin, wo das Pult geöffnet wurde: aus dem Gespräch ins Skript, aus dem
        // Abschluss in den Abschluss. Sonst stünde man nach dem Schreiben im falschen Bild.
        if (e.key === 'Escape') { e.preventDefault(); mailPultSchliessen(); }
        return; // Betreff und Text sind Eingabefelder
      }

      if (mode === 'confirm') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); return; }
        if (typing) return;   // im datetime-Feld darf getippt werden
        if (e.key === 'Enter') { e.preventDefault(); verwerfen(); return; }
        // Dieselben Tasten wie im Abschluss-Panel und im Kurzanruf-Dialog. Bis hierher lag
        // „Niemand rangegangen" auf 'X' — und seit 'X' im Panel 'NUMMER FALSCH' bedeutet, hätte
        // dieselbe Taste in zwei Dialogen zwei verschiedene Ergebnisse gespeichert. Für den
        // Kurzanruf-Dialog wurde das am 2026-08-12 schon behoben; dieser hier blieb übrig, und mit
        // einem zweiten Anschluss-Befund im selben Dialog wäre die Verwechslung teuer geworden.
        // 'X' ist jetzt unbelegt statt falsch belegt: ein Tastendruck ins Leere kostet einen Blick,
        // ein falsch gespeichertes Ergebnis kostet einen Lead.
        if (e.key.toLowerCase() === CALL_OUTCOME_META['NICHT ERREICHT'].hotkey.toLowerCase()) {
          e.preventDefault(); quickNoAnswer(); return;
        }
        if (e.key.toLowerCase() === CALL_OUTCOME_META['NUMMER NICHT VERGEBEN'].hotkey.toLowerCase()) {
          e.preventDefault(); quickNummerNichtVergeben(); return;
        }
        // Ziffern wählen die Wiedervorlage. Hier sind sie frei — im Abschluss-Panel gehören
        // 1-4 den Emotionen, dort geht die Wahl nur per Klick.
        if (/^[1-9]$/.test(e.key) && presets[+e.key - 1]) { e.preventDefault(); waehlePreset(+e.key - 1); return; }
        return;
      }

      if (mode === 'shortcall') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); return; }
        if (typing) return;
        if (e.key === 'Enter') { e.preventDefault(); setMode('save'); return; } // trotzdem tracken
        // Dieselbe Taste wie 'NICHT ERREICHT' im Abschluss-Panel — der Knopf hier speichert genau
        // das. Bis 2026-08-12 lag er auf 'X', und seit es dort 'NUMMER FALSCH' heisst, hätte
        // dieselbe Taste in zwei Dialogen zwei verschiedene Ergebnisse gespeichert.
        if (e.key.toLowerCase() === CALL_OUTCOME_META['NICHT ERREICHT'].hotkey.toLowerCase()) {
          e.preventDefault(); quickNoAnswer(); return;
        }
        if (e.key.toLowerCase() === CALL_OUTCOME_META['NUMMER NICHT VERGEBEN'].hotkey.toLowerCase()) {
          e.preventDefault(); quickNummerNichtVergeben(); return;
        }
        if (/^[1-9]$/.test(e.key) && presets[+e.key - 1]) { e.preventDefault(); waehlePreset(+e.key - 1); return; }
        return;
      }

      if (mode === 'save') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); return; }
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); save(); return; }
        if (typing) return;
        const k = e.key.toLowerCase();
        if (OUTCOME_KEY[k]) { e.preventDefault(); setOutcome((o) => (o === OUTCOME_KEY[k] ? null : OUTCOME_KEY[k])); return; }
        if (['1', '2', '3', '4'].includes(e.key)) { e.preventDefault(); toggleEmotion(EMOTIONS[+e.key - 1]); return; }
        if (e.key === 'Enter') { e.preventDefault(); save(); return; }
        return;
      }

      // --- call-mode ---
      const key = e.key;
      const lower = key.toLowerCase();

      // Schriftgrad direkt aus dem Gespräch heraus, ohne die Maus zu suchen: die Zeile, die man
      // gerade vorliest, ist genau die, bei der auffällt, dass sie zu klein ist. '=' liegt auf
      // derselben Taste wie '+' (ohne Umschalt), '0' setzt zurück.
      if (key === '+' || key === '=') { e.preventDefault(); groesser(); return; }
      if (key === '-' || key === '_') { e.preventDefault(); kleiner(); return; }
      if (key === '0') { e.preventDefault(); setSchriftIdx(SCHRIFT_STANDARD); return; }

      // Ziffer 1–9 → direkt zum passenden Einwand dieser Sektion springen (Detour).
      if (key === 'ArrowDown') { e.preventDefault(); startScroll(SCROLL_SPEED); return; }
      if (key === 'ArrowUp') { e.preventDefault(); startScroll(-SCROLL_SPEED); return; }
      // Beide Abzweigungs-Reihen an EINER Stelle, und zwar vor den Buchstaben-Kürzeln weiter
      // unten: sonst öffnete das Q einer Ablehnung nichts, weil kein Fall es abfängt — und das O
      // fiele in die Seite darunter durch.
      const zielBranch = branchVonTaste.get(lower);
      if (zielBranch) {
        e.preventDefault();
        if (fullText) setFullText(false);
        zurAbzweigung(zielBranch);
        return;
      }
      // Eine Ziffer ohne Einwand dahinter bleibt trotzdem beim Cockpit: sie soll nicht als
      // Stapel-Befehl in der Seite darunter landen (callKeys.ts).
      if (/^[1-9]$/.test(key)) return;

      if (key === 'ArrowRight' || key === ' ' || key === 'Enter') {
        // „Weiter" heisst immer dasselbe: einen Schritt vorwärts auf dem Weg, auf dem man gerade
        // ist. In einer Abzweigung ist das erst die nächste STUFE des Einwands (ARA → ACE) und
        // erst danach der Rücksprung ins Skript — vorher sprang derselbe Tastendruck über ACE
        // hinweg, das man ja gerade brauchen wollte.
        e.preventDefault();
        if (onBranch && naechsterTeil) setTeilIdx(teilNr + 1);
        else if (onBranch) zurAbzweigung(null);
        else if (idx >= total - 1) goToSave();
        else setMainIdx((i) => Math.min(i + 1, total - 1));
      } else if (key === 'ArrowLeft') {
        e.preventDefault();
        if (onBranch && teilNr > 0) setTeilIdx(teilNr - 1);
        else if (onBranch) zurAbzweigung(null);
        else setMainIdx((i) => Math.max(i - 1, 0));
      } else if (key === 'g') {
        e.preventDefault(); zurAbzweigung(null); setMainIdx(0);
      } else if (key === 'G') {
        e.preventDefault(); zurAbzweigung(null); setMainIdx(Math.max(0, total - 1));
      } else if (lower === 'b') {
        e.preventDefault(); zurAbzweigung(null); setMainIdx(total - 1); // direkt zur Buchungs-Sektion
      } else if (lower === 'n') {
        e.preventDefault(); setMode('notes'); setTimeout(() => notesRef.current?.focus(), 0);
      } else if (lower === 'm') {
        // Frei geworden, als 'MAILBOX' seine Taste bekam? Nein: 'M' gehört im ABSCHLUSS der Mailbox
        // (CALL_OUTCOME_META), hier im Gespräch ist sie unbelegt. Zwei verschiedene Bilder, zwei
        // verschiedene Bedeutungen — das ist die bestehende Ordnung (S = speichern vs. Sekretariat).
        e.preventDefault();
        if (mailLeadId) { setMailZurueck('call'); setMode('mail'); }
      } else if (lower === 'i') {
        e.preventDefault(); setMode('info');
      } else if (lower === 's') {
        e.preventDefault(); goToSave();
      } else if (lower === 'x') {
        e.preventDefault(); quickNoAnswer(); // nicht rangegangen → sofort speichern & beenden
      } else if (key === 'Escape') {
        e.preventDefault();
        if (onBranch) zurAbzweigung(null); else setMode('confirm');
      }
    };
    // CAPTURE (`true`): das Cockpit muss VOR den Handlern der Seiten drankommen, die unter ihm
    // gemountet bleiben — sonst nützt `stopPropagation` oben nichts.
    // Gehorcht wird dem Fenster, IN dem das Cockpit steht. Ausgelagert ist das ein anderes
    // `window` — bliebe der Handler am Tab, blaetterte man im Popup ins Leere, und die Tasten
    // faenden im Tab den Telefon-Stapel darunter. Genau der Griff, gegen den `callKeys.ts` gebaut
    // ist, nur andersherum.
    const ziel: Window = popFenster ?? window;
    ziel.addEventListener('keydown', onKey, true);
    // Pfeil hoch/runter: weich scrollen, solange die Taste gehalten wird (nicht springen).
    const onKeyUp = (e: KeyboardEvent) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') stopScroll(); };
    ziel.addEventListener('keyup', onKeyUp);
    return () => {
      ziel.removeEventListener('keydown', onKey, true);
      ziel.removeEventListener('keyup', onKeyUp);
    };
  }, [mode, idx, total, onBranch, visibleBranches, branchVonTaste, fullText, save, goToSave, quickNoAnswer,
      teilNr, naechsterTeil, zurAbzweigung,
      quickNummerNichtVergeben, toggleEmotion,
      endCall, verwerfen, startScroll, stopScroll, presets, waehlePreset, popFenster, mailLeadId,
      mailZurueck, mailPultSchliessen, groesser, kleiner]);

  // follow_up_date trägt seit der Zeitwahl den vollen Zeitstempel (der Server schreibt ihn aus
  // callback_at); alte taggenaue Werte liest labelFor genauso.
  const naechsterRueckruf = labelFor(liveLead?.follow_up_date ?? activeLead?.follow_up_date);

  // „Kollegin ist gerade dran" / „hat vor 5 Min. gewählt". Aus dem Wählzusammenhang, den
  // `useTrackedDial` mitgebracht hat — er wurde beim Wählen einmal ermittelt und ändert sich
  // während des Gesprächs nicht mehr. Das ist Absicht: die Auskunft beschreibt den Stand, mit dem
  // man in dieses Gespräch gegangen ist. Ein mitlaufender Zähler wäre eine zweite Uhr im Cockpit,
  // die niemand gefragt hat, und der Fall „ein Dritter wählt WÄHREND meines Gesprächs" ist genau
  // der, den die Sperre in der Tabelle ohnehin verhindert.
  const fremdImGespraech = (() => {
    const o = aktuelleWahl?.other_caller;
    if (!o?.at) return null;
    const then = new Date(o.at.replace(' ', 'T')).getTime();
    if (Number.isNaN(then)) return null;
    const min = Math.max(0, Math.round((Date.now() - then) / 60000));
    const wer = o.name || 'Jemand';
    // Der Name steht direkt darüber — „ist hier gerade im Gespräch" genügt und passt auch ins Fenster.
    if (o.open && min < 20) return { text: `${wer} ist hier gerade im Gespräch`, laeuft: true };
    const wann = min < 60 ? `vor ${min} Min.`
      : min < 60 * 24 ? `vor ${Math.round(min / 60)} Std.`
      : `vor ${Math.round(min / (60 * 24))} Tg.`;
    return { text: `${wer} hat ${wann} hier angerufen`, laeuft: false };
  })();

  if (!activeLead) return null;

  // Zusammengeklappt: nur die Marke. Das Bauteil bleibt gemountet — Timer, Notizen, Outcome und die
  // Skript-Position laufen unsichtbar weiter und stehen beim Zurückholen unverändert da.
  // Die Marke am Rand. Zwei Anlässe, dieselbe Form: zusammengeklappt IM Tab — und ausgelagert,
  // wo im Tab sonst gar nichts stünde. Der zweite Fall ist der wichtigere: die Telefon-Tabelle
  // steht während eines Gesprächs still (Nutzerentscheidung), und ohne Marke sähe man im Tab eine
  // angehaltene Liste ohne erkennbaren Grund. Der Klick holt dann nicht zurück, sondern hebt das
  // ausgelagerte Fenster nach vorn — es ist ja nicht weg, es liegt nur hinter etwas.
  const marke = (beiKlick: () => void, hinweis: string) => (
      <button
        onClick={beiKlick}
        title={hinweis}
        // Über allen Fenstern (die liegen bei 60 + Anzahl): die Marke ist der einzige Weg zurück in
        // den laufenden Anruf — verdeckt wäre sie schlimmer als gar nicht da.
        className={`call-mono ${callDark ? '' : 'call-light'} fixed bottom-5 right-5 z-[65] inline-flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full border border-stark bg-bg-surface/95 backdrop-blur shadow-lg transition-colors`}
      >
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-accent/60 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
        </span>
        <span className="text-sm font-semibold text-text-main">Call läuft</span>
        <span className="font-mono text-sm tabular-nums text-accent">{fmt(elapsed)}</span>
        {isRecording && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500" title="Aufnahme läuft">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> REC
          </span>
        )}
        <span className="max-w-[10rem] truncate text-xs text-text-muted">
          {angerufenerName}
        </span>
      </button>
  );

  if (modus === 'min') {
    // Zurückholen heisst zugleich nach vorn: sonst käme das Cockpit hinter einem
    // Nachschlage-Fenster wieder hoch, das man vorher davor geschoben hatte.
    return marke(() => { nachVorn(); setCallWindowMode('window'); },
                 `Anruf mit ${angerufenerName} läuft · zurückholen`);
  }

  const rahmen = (
    <div
      ref={rootRef}
      tabIndex={-1}
      // Hineinklicken heisst: die Kürzel gehören jetzt wieder dem Cockpit — und es liegt vorn.
      // `Capture`, damit auch ein Klick auf einen Knopf darin zählt.
      onPointerDownCapture={nachVorn}
      onPointerDown={() => { if (imFenster) rootRef.current?.focus({ preventScroll: true }); }}
      onFocus={() => setAktiv(true)}
      onBlur={(e) => { if (!rootRef.current?.contains(e.relatedTarget as Node)) setAktiv(false); }}
      style={{ ...fensterStil, zIndex: ebene }}
      // Eigenes, streng monochromes Theme (index.css, .call-mono) mit eigenem Hell/Dunkel-Schalter.
      // Das Cockpit haengt als Geschwister der Seiten-Spalte in der AppShell und erbt deren
      // Theme-Klassen ohnehin nicht — es braucht also eigene Tokens, dann gleich die richtigen.
      className={`call-mono ${callDark ? '' : 'call-light'} ${imFenster
        ? `fixed bg-bg-primary text-text-main flex flex-col overflow-hidden outline-none rounded-2xl shadow-2xl border ${
            aktiv ? 'border-stark ring-1 ring-accent/20 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.45)]' : 'border-border-primary'}`
        : 'fixed inset-0 bg-bg-primary text-text-main flex flex-col overflow-hidden outline-none'}`}
    >
      {/* Der Cyan-Schimmer über dem Kopf ist ersatzlos weg (2026-08-21): eine farbige Wolke auf der
          Fläche, auf der man liest, ist genau die Ablenkung, gegen die das monochrome Cockpit
          gebaut ist — und den Kontrast der ersten Skript-Zeilen hat sie mitgenommen. */}

      {/* HUD oben · im Fenster zugleich der Griff zum Verschieben */}
      <div
        {...ziehGriff}
        className={`relative flex items-center justify-between overflow-hidden border-b border-border-primary ${
          imFenster ? 'px-3 py-2 gap-2 cursor-move select-none' : 'px-6 py-4'}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 rounded-full bg-accent/12 text-accent grid place-items-center font-semibold ring-1 ring-accent/20 ${
                imFenster ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm'}`}>
            {initials}
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate leading-tight">{angerufenerName}</div>
            <div className={`text-text-muted truncate ${imFenster ? 'text-xs' : 'text-sm'}`}>
              {liveLead?.company ?? activeLead.company}
            </div>
            {/* „Da ist gerade jemand anders dran." Steht an der PERSON, nicht in der Fusszeile: es
                ändert, wie man das Gespräch eröffnet — wer weiss, dass eine Kollegin vor zehn
                Minuten hier war, fängt nicht bei null an. Bleibt auch im Fenstermodus stehen,
                anders als der Rückruf-Hinweis: dass jemand parallel telefoniert, ist die
                dringendere Auskunft von beiden. */}
            {fremdImGespraech && (
              <div className={`truncate text-xs font-medium flex items-center gap-1.5 ${
                fremdImGespraech.laeuft ? 'text-text-main' : 'text-text-muted/80'}`}
                   title={fremdImGespraech.laeuft
                     ? 'Gewählt und noch nicht abgeschlossen — vermutlich läuft das Gespräch gerade'
                     : 'Aus dem Anruf-Verlauf dieser Person'}>
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                  fremdImGespraech.laeuft ? 'bg-text-main animate-pulse' : 'bg-text-muted/50'}`} />
                {fremdImGespraech.text}
              </div>
            )}
            {/* Was beim letzten Anruf verabredet wurde. Nur eine Zeile, kein Klickziel — die
                Warteschlange selbst steht im Telefon-Stapel. Im Fenster fällt sie weg: die
                Kopfleiste ist dort der Griff und darf nicht drei Zeilen hoch sein. */}
            {naechsterRueckruf && !imFenster && (
              <div className="text-xs text-text-muted/70 truncate">nächster Rückruf: {naechsterRueckruf}</div>
            )}
          </div>
        </div>
        {/* Rechte Kopfleiste, ab 2026-08-21 in drei Gruppen statt einer Reihe aus sieben gleich
            aussehenden Symbolen. Der Ausloeser war das X: es stand MITTEN in der Reihe (zwischen
            Vollbild und „ganzes Skript"), also genau dort, wo man blind hinklickt, um das Skript
            umzuschalten — und beendete stattdessen das Gespraech. Jetzt sitzt es allein ganz
            aussen, hinter einem Trenner, und faerbt sich beim Hoveren rot.
            Reihenfolge = Haeufigkeit: was man IM Gespraech braucht (Skript, Info, Mail) links,
            die Fenster-Griffe daneben, das Beenden zuletzt. */}
        <div className={`flex shrink-0 items-center ${imFenster ? 'gap-1' : 'gap-2 sm:gap-3'}`}>
          {/* Laufzeit + Position als EIN Feld: beides beantwortet „wo stehe ich gerade" und stand
              vorher als zwei lose Zahlengruppen nebeneinander. */}
          {/* Im Fenster ohne Pillen-Rahmen: dort steht neben der Uhr nur noch sie selbst, und bei
              380 px Fensterbreite ist jeder Rahmen Platz, der dem Namen des Angerufenen fehlt. */}
          <div className={`flex items-center ${
                imFenster ? 'gap-2' : 'gap-3 rounded-full border border-border-primary bg-bg-surface/70 px-3 py-1'}`}>
            <span className="flex items-center gap-2 font-mono text-accent tabular-nums text-[0.95rem]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent/60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              {fmt(elapsed)}
            </span>
            {isRecording && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-500" title="Aufnahme läuft — wird nach dem Speichern hochgeladen">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> REC
              </span>
            )}
            {!imFenster && (
              <>
                <span className="h-3.5 w-px bg-border-primary" />
                <span className="font-mono text-sm tabular-nums">
                  <span className="text-accent font-semibold">{String(Math.min(idx + 1, total)).padStart(2, '0')}</span>
                  <span className="text-text-muted"> / {String(total).padStart(2, '0')}</span>
                </span>
              </>
            )}
          </div>
          {onBranch && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-accent/12 text-accent border border-accent/25">
              Einwand
            </span>
          )}

          {/* Gruppe 0 · Lesbarkeit. Steht VOR den Werkzeugen, weil man sie einmal am Anfang des
              Tages anfasst und dann nie wieder — und weil beides denselben Gegenstand hat: wie gut
              man den Text da unten lesen kann. Beide Einstellungen ueberleben den Neustart. */}
          <div className={`flex items-center rounded-lg border border-border-primary ${
                imFenster ? 'ml-1' : 'ml-2'}`}>
            <button
              onClick={kleiner}
              disabled={amMinimum}
              className={`rounded-l-lg font-semibold leading-none text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors disabled:opacity-25 disabled:hover:bg-transparent ${
                imFenster ? 'px-1.5 py-1 text-[11px]' : 'px-2 py-1.5 text-xs'}`}
              aria-label="Schrift kleiner"
              title="Schrift kleiner (−)"
            >
              A
            </button>
            <span className="self-stretch w-px bg-border-primary" />
            <button
              onClick={groesser}
              disabled={amMaximum}
              className={`rounded-r-lg font-semibold leading-none text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors disabled:opacity-25 disabled:hover:bg-transparent ${
                imFenster ? 'px-1.5 py-1 text-[15px]' : 'px-2 py-1.5 text-base'}`}
              aria-label="Schrift grösser"
              title="Schrift grösser (+) · 0 setzt zurück"
            >
              A
            </button>
          </div>
          <button
            onClick={() => setCallDark((v) => !v)}
            className={`rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors ${
              imFenster ? 'p-1' : 'p-1.5'}`}
            aria-label={callDark ? 'Heller Modus' : 'Dunkler Modus'}
            title={callDark ? 'Heller Modus · Weiss' : 'Dunkler Modus · Schwarz'}
          >
            {callDark ? <Sun size={imFenster ? 16 : 20} /> : <Moon size={imFenster ? 16 : 20} />}
          </button>

          {/* Gruppe 1 · Werkzeuge fuers laufende Gespraech */}
          <span className={`self-stretch w-px bg-border-primary ${imFenster ? 'my-1' : 'my-1.5'}`} />
          <div className={`flex items-center ${imFenster ? 'gap-0.5' : 'gap-1'}`}>
            <button
              onClick={() => setFullText((v) => !v)}
              className={`rounded-lg transition-colors ${imFenster ? 'p-1' : 'p-1.5'} ${
                fullText ? 'text-accent bg-accent/12' : 'text-text-muted hover:text-text-main hover:bg-bg-elevated'
              }`}
              aria-label={fullText ? 'Fokus-Ansicht' : 'Ganzes Skript'}
              title={fullText ? 'Fokus (eine Sektion)' : 'Ganzes Skript anzeigen'}
            >
              <AlignLeft size={imFenster ? 16 : 20} />
            </button>
            <button
              onClick={() => setMode('info')}
              className={`rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors ${
                imFenster ? 'p-1' : 'p-1.5'}`}
              aria-label="Info & Kontakt"
              title="Kontakt & Firmendaten (I)"
            >
              <InfoIcon size={imFenster ? 16 : 20} />
            </button>
            {/* Die Mail MITTEN im Gespräch, nicht erst im Abschluss. „Schicken Sie mir mal was" fällt,
                während man noch am Ohr ist — und genau dann will man sagen können „mache ich sofort,
                haben Sie noch die Adresse …". Wer erst auflegt und dann sucht, tippt die Adresse aus
                dem Gedächtnis. Der Abschluss behält seinen eigenen Knopf: dort ist der Vermerk zu
                Hause. Beide öffnen dasselbe Pult, und Esc führt dorthin zurück, wo man herkam. */}
            <button
              onClick={() => { setMailZurueck('call'); setMode('mail'); }}
              disabled={!mailLeadId}
              className={`rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                imFenster ? 'p-1' : 'p-1.5'} ${
                mailVermerkt ? 'text-text-main bg-bg-elevated'
                             : 'text-text-muted hover:text-text-main hover:bg-bg-elevated'}`}
              aria-label="Mail schreiben"
              title={!mailLeadId ? 'Kein verknüpfter fraud0-Lead — keine Mail möglich'
                     : mailVermerkt ? 'Mail ist raus (M)' : 'Mail schreiben (M)'}
            >
              {mailVermerkt ? <Check size={imFenster ? 16 : 20} /> : <Mail size={imFenster ? 16 : 20} />}
            </button>
          </div>

          {/* Gruppe 2 · Fenster-Griffe. Auf schmalen Geraeten faellt sie ganz weg — dort gibt es
              kein Daneben, und ein Trenner vor einer leeren Gruppe waere ein Strich ins Nichts. */}
          {!schmal && (
            <>
              <span className={`self-stretch w-px bg-border-primary ${imFenster ? 'my-1' : 'my-1.5'}`} />
              <div className={`flex items-center ${imFenster ? 'gap-0.5' : 'gap-1'}`}>
                {/* Aus dem Tab ziehen: eigenes Fenster, zweiter Bildschirm. Derselbe Anruf — dasselbe
                    Bauteil wandert nur ins andere Dokument, Timer und Skript-Position laufen weiter. */}
                <button
                  onClick={() => setCallWindowMode(ausgelagert ? 'window' : 'popout')}
                  className={`rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors ${
                    imFenster ? 'p-1' : 'p-1.5'}`}
                  aria-label={ausgelagert ? 'Zurück in den Tab' : 'Aus dem Tab ziehen'}
                  title={ausgelagert ? 'Zurück in den Tab'
                                     : 'Aus dem Tab ziehen · eigenes Fenster, z. B. zweiter Bildschirm'}
                >
                  {ausgelagert ? <CornerDownLeft size={imFenster ? 16 : 20} />
                               : <ExternalLink size={imFenster ? 16 : 20} />}
                </button>
                {/* Zusammenklappen beendet NICHTS — der Anruf läuft weiter, nur eben als Marke am
                    Rand. Deshalb ein eigener Knopf und nicht statt des X: „weg" und „beendet" müssen
                    zwei verschiedene Handgriffe bleiben. Zusammenklappen und Vollbild sind im
                    eigenen Fenster gegenstandslos — das macht dort das Betriebssystem. */}
                {!ausgelagert && (
                  <button
                    onClick={() => setCallWindowMode('min')}
                    className={`rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors ${
                      imFenster ? 'p-1' : 'p-1.5'}`}
                    aria-label="Zusammenklappen"
                    title="Zusammenklappen · der Anruf läuft weiter"
                  >
                    <Minus size={imFenster ? 16 : 20} />
                  </button>
                )}
                {!ausgelagert && (
                  <button
                    onClick={() => setCallWindowMode(imFenster ? 'full' : 'window')}
                    className={`rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors ${
                      imFenster ? 'p-1' : 'p-1.5'}`}
                    aria-label={imFenster ? 'Vollbild' : 'Als Fenster'}
                    title={imFenster ? 'Vollbild' : 'Als Fenster · daneben weiterarbeiten'}
                  >
                    {imFenster ? <Maximize2 size={16} /> : <Minimize2 size={20} />}
                  </button>
                )}
              </div>
            </>
          )}

          {/* Gruppe 3 · Beenden. Allein, ganz aussen, rot beim Hoveren: der einzige Knopf hier oben,
              der das Gespraech wegnimmt, soll auch der einzige sein, den man nicht versehentlich
              trifft. */}
          <span className={`self-stretch w-px bg-border-primary ${imFenster ? 'my-1' : 'my-1.5'}`} />
          <button
            onClick={() => setMode('confirm')}
            className={`rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors ${
              imFenster ? 'p-1' : 'p-1.5'}`}
            aria-label="Schliessen"
            title="Anruf beenden (Esc)"
          >
            <X size={imFenster ? 16 : 20} />
          </button>
        </div>
      </div>

      {/* Fokus: aktueller Abschnitt, gross & lesbar, vertikal zentriert · oder das ganze Skript am Stück
          (Volltext), das sich per Pfeiltaste weich weiterscrollen lässt. */}
      <div className="relative flex-1 overflow-hidden">
        {/* Skript (scrollt) · beim Buchen unsichtbar, bleibt aber im DOM (Position + Scrollstand bleiben) */}
        <div ref={scrollRef} className={`absolute inset-0 overflow-y-auto ${isBooking ? 'invisible pointer-events-none' : ''}`}>
        {fullText ? (
          // „Ganzes Skript" heisst ab 2026-08-17 wirklich das ganze: erst der Ablauf, dann die
          // Einwände als eigener Bereich. Vorher standen hier nur die Flow-Blöcke — die Einwände
          // waren ausschliesslich über die Knöpfe der aktuellen Sektion erreichbar, und wer im
          // Gespräch schnell nachlesen wollte, was auf „zu teuer" zu antworten ist, fand es hier
          // nicht. Zwei Überschriften, dieselbe Trennung wie beim Eintragen in der Bibliothek.
          <div className={`w-full mx-auto ${imFenster ? 'px-5 py-6 space-y-10' : 'px-8 py-12 space-y-14'}`}>
            {([
              { keys: flow.filter((k) => k !== BOOKING_KEY), label: 'Ablauf' },
              { keys: branchKeys, label: 'Einwände' },
            ] as const).filter((b) => b.keys.length).map((bereich) => (
              <div key={bereich.label} className={imFenster ? 'space-y-8' : 'space-y-12'}>
                {/* Die Überschrift steht nur da, wenn es auch etwas zu trennen GIBT — bei einem
                    Skript ohne Einwände wäre „Ablauf" eine Rubrik ohne Gegenstück. Beide Rubriken
                    sehen gleich aus: unterschieden werden sie durch ihr Wort, nicht durch Farbe. */}
                {branchKeys.length > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="h-px w-8 bg-text-muted" />
                    <span className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted">
                      {bereich.label}
                    </span>
                  </div>
                )}
                {bereich.keys.map((k) => {
                  const sec = data[k] ?? { title: '', body: '' };
                  const body = fillPlaceholders(sec.body || '', fillCtx);
                  return (
                    <section key={k}>
                      <h2 className="font-bold mb-4 leading-tight tracking-tight"
                          style={{ fontSize: `calc(${gradTitel} * 0.72)` }}>
                        {sec.title || <span className="text-text-muted">Ohne Titel</span>}
                      </h2>
                      {/* Auch hier die Stufen als Stufen: im Volltext standen die Marken bis
                          2026-08-21 als roher Text mitten im Skript („@@ ARA @@"). Gestapelt statt
                          durchgeblättert — wer das ganze Skript aufmacht, will nachlesen, nicht
                          bedienen. */}
                      {body ? (
                        skriptTeile(body).map((teil, ti) => (
                          <div key={ti} className={ti ? (imFenster ? 'mt-5' : 'mt-7') : undefined}>
                            {teil.marke && (
                              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-text-muted">
                                {teil.marke}
                              </div>
                            )}
                            <div className="whitespace-pre-wrap text-text-main"
                                 style={{ fontSize: gradText, lineHeight: 1.6 }}>{renderScript(teil.text)}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-text-muted italic" style={{ fontSize: gradText }}>Kein Text · frei sprechen.</div>
                      )}
                    </section>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
        // Der Schriftgrad kommt seit 2026-08-21 aus dem Regler oben (gradTitel/gradText) statt aus
        // festen Tailwind-Stufen. Grund war nicht Geschmack: dieselbe Ansicht wird auf einem
        // 13-Zoll-Laptop und auf einem 27-Zoll-Schirm am Stehtisch vorgelesen, und wer das Skript
        // nicht lesen kann, liest es nicht vor, sondern erfindet es.
        <div className="min-h-full flex items-center">
          <div className={`w-full mx-auto ${imFenster ? 'max-w-none px-5 py-6' : 'max-w-3xl px-8 py-16'}`}>
            <div>
              <div className={`flex items-center gap-3 ${imFenster ? 'mb-3' : 'mb-5'}`}>
                <span className="h-px w-8 bg-text-muted" />
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted">
                  {onBranch ? 'Einwand · Abzweigung' : `Sektion ${idx + 1}`}
                </span>
              </div>
              <h1 className={`font-bold leading-[1.08] tracking-tight ${imFenster ? 'mb-4' : 'mb-8'}`}
                  style={{ fontSize: gradTitel }}>
                {cur.title || <span className="text-text-muted">Ohne Titel</span>}
              </h1>
              {/* Der Text der aktuellen STUFE, nicht der ganze Block. Ausserhalb einer Abzweigung
                  hat ein Block genau eine Stufe, dort ändert das nichts. */}
              {onBranch && teile.length > 1 && (
                <div className={`flex items-center gap-2 ${imFenster ? 'mb-2' : 'mb-4'}`}>
                  {teile.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => setTeilIdx(i)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-[0.12em] border transition-colors ${
                        i === teilNr ? 'bg-accent border-accent'
                                     : 'border-border-primary text-text-muted hover:text-text-main hover:bg-bg-elevated'}`}
                      title={i === teilNr ? 'Aktuelle Stufe' : `Zu Stufe ${i + 1} springen`}
                    >
                      {t.marke ?? `Stufe ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
              {/* Volle Textfarbe statt der frueheren 80 % — auf Schwarz war der Vorlesetext damit
                  grau, und Grau ist im monochromen Cockpit die Farbe der NEBENsachen (Kürzel,
                  Rubriken, erwartete Antworten des Leads). Was man spricht, steht in voller Deckung. */}
              {aktuellerTeil.text ? (
                <div className="whitespace-pre-wrap text-text-main"
                     style={{ fontSize: gradText, lineHeight: 1.6 }}>
                  {renderScript(aktuellerTeil.text)}
                </div>
              ) : (
                <div className="text-text-muted italic" style={{ fontSize: gradText }}>
                  {onBranch && teile.length > 1 ? 'Diese Stufe ist noch leer.' : 'Kein Text in dieser Sektion. Frei sprechen.'}
                </div>
              )}

              {/* Was man AUS einem Einwand heraus tun kann. Bis 2026-08-21 stand hier genau ein
                  Knopf: „zurück ins Skript". Das bildete das Gespräch nicht ab — ein Einwand ist
                  kein Umweg, den man abhakt, sondern eine eigene kleine Verhandlung mit drei
                  Ausgängen:
                    · die zweite Stufe desselben Einwands (ARA hat nicht getragen → ACE),
                    · der nächste Einwand (nach ACE kommt selten Ruhe, sondern der nächste),
                    · der Termin (jede Stufe endet mit „passt es dir Ende der Woche?").
                  Deshalb ist die nächste Stufe jetzt der HAUPTknopf und nicht der Rücksprung: sie
                  ist der wahrscheinlichste nächste Griff, der Rücksprung der seltenste. */}
              {onBranch ? (
                <div className={imFenster ? 'mt-8' : 'mt-12'}>
                  <div className="flex flex-wrap items-center gap-2">
                    {naechsterTeil ? (
                      <button
                        onClick={() => setTeilIdx(teilNr + 1)}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-accent text-sm font-semibold hover:bg-accent-hover transition-colors"
                      >
                        {naechsterTeil.marke ? `Weiter zu ${naechsterTeil.marke}` : 'Nächste Stufe'} <span className="opacity-60">→</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => zurAbzweigung(null)}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-accent text-sm font-semibold hover:bg-accent-hover transition-colors"
                      >
                        Zurück ins Skript <span className="opacity-60">→</span>
                      </button>
                    )}
                    {/* Der Termin gehört an JEDEN Einwand, nicht nur ans Ende des Skripts: jede
                        Stufe läuft auf „passt es dir Ende der Woche?" hinaus, und wenn das Ja
                        kommt, darf der Kalender nicht drei Tastendrücke weit weg sein. */}
                    <button
                      onClick={() => { zurAbzweigung(null); setMainIdx(total - 1); }}
                      className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-stark bg-bg-surface hover:bg-bg-elevated text-sm font-medium text-text-main transition-colors"
                    >
                      Termin buchen <Kbd>B</Kbd>
                    </button>
                    {naechsterTeil && (
                      <button
                        onClick={() => zurAbzweigung(null)}
                        className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-stark bg-bg-surface hover:bg-bg-elevated text-sm font-medium text-text-main transition-colors"
                      >
                        Zurück ins Skript
                      </button>
                    )}
                    {/* Antwortkette aus block_meta.next („kein Interesse" Stufe 1 → 2 → 3). Ein
                        ANDERER Block, nicht die nächste Stufe im selben — deshalb daneben und
                        nicht statt ihrer. */}
                    {nextStageKey && (
                      <button
                        onClick={() => zurAbzweigung(nextStageKey)}
                        className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-stark bg-bg-surface hover:bg-bg-elevated text-sm text-text-main transition-colors"
                      >
                        Nächste Stufe → <span className="text-text-muted">{branchLabel(data[nextStageKey]?.title || nextStageKey)}</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : canSkipOptional ? (
                <div className={imFenster ? 'mt-8' : 'mt-14'}>
                  <button
                    onClick={() => { zurAbzweigung(null); setMainIdx(firstRequiredIdx); }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-stark bg-bg-surface hover:bg-bg-elevated text-sm text-text-main transition-colors"
                  >
                    Direkt durchgestellt → <span className="text-text-muted">{data[flow[firstRequiredIdx]]?.title ?? ''}</span>
                  </button>
                </div>
              ) : null}
                  {/* Die Einwände waren bis 2026-08-21 blasse Pillen am Fussende: derselbe Grau-Ton
                      wie die Kürzelleiste, die Ziffer als 11-Pixel-Marke darin. Genau die will man
                      aber im Moment des Einwands FINDEN, ohne zu suchen — „zu teuer" kommt nicht
                      angekündigt.
                      Karten allein reichten nicht: bei achtzehn Abzweigungen entstand eine Wand aus
                      fast gleichlautenden Zeilen, weil jeder Titel seine Rubrik mitbrachte
                      („Ablehnung: …", „Einwand: …") und abgeschnitten wurde ausgerechnet das Ende,
                      an dem sie sich unterscheiden. Jetzt trägt die Rubrik EINE Zwischenzeile, die
                      Karte nur noch die Sache — und die Gruppen liegen in derselben Reihenfolge wie
                      die Tasten. */}
                  {visibleBranches.length > 0 && (
                    <div className={imFenster ? 'mt-8' : 'mt-14'}>
                    <div className="border-t border-border-primary pt-5">
                      <div className="flex items-baseline gap-3 mb-4">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-text-muted">
                          {onBranch ? 'Weiter zu einem anderen Einwand' : 'Mögliche Einwände'}
                        </span>
                        <span className="text-xs text-text-muted">{visibleBranches.length}</span>
                      </div>
                      {branchGruppen.map((gruppe) => (
                        <div key={gruppe.rubrik ?? '__ohne__'} className="mb-4 last:mb-0">
                          {/* Die Rubrik steht nur da, wenn es auch mehr als eine gibt — bei einem
                              Skript ohne Rubriken wäre „Weitere" eine Überschrift über allem. */}
                          {branchGruppen.length > 1 && (
                            <div className="mb-1.5 flex items-baseline gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted/70">
                              {gruppe.rubrik ?? 'Weitere'}
                              {/* Welche Reihe hier gilt, steht an der Gruppe und nicht in der
                                  Fusszeile: dort stünde es zweimal und beide Male ohne Bezug. */}
                              <span className="font-mono normal-case tracking-normal text-text-muted/60">
                                {gruppe.keys.some((k) => istAblehnung(data[k]?.title || k)) ? 'Q W E R T Z …' : '1 2 3 …'}
                              </span>
                            </div>
                          )}
                          <div className={`grid gap-1.5 ${imFenster ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {gruppe.keys.map((k) => {
                              const taste = tasteVon.get(k);
                              const sache = branchLabel(data[k]?.title || k);
                              // Der Einwand, in dem man GERADE steht, bleibt in der Liste stehen
                              // statt zu verschwinden: sonst rutschten alle Ziffern um eins, und
                              // die Taste 3 träfe plötzlich einen anderen Block als eben noch.
                              // Markiert statt entfernt — man sieht, wo man ist. Ein Klick darauf
                              // setzt ihn auf seine erste Stufe zurück (ARA), was aus ACE heraus
                              // genau der Griff ist, den man sucht.
                              const istAktiv = onBranch && k === branchKey;
                              return (
                                <button
                                  key={k}
                                  onClick={() => zurAbzweigung(k)}
                                  // Der volle Titel als Tooltip: was in der Zeile nicht mehr
                                  // hinpasst, ist damit wenigstens erreichbar, ohne den Block zu
                                  // öffnen.
                                  title={data[k]?.title || k}
                                  className={`group flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-lg border text-left transition-colors ${
                                    istAktiv ? 'border-accent bg-bg-elevated ring-1 ring-accent/40'
                                             : 'border-stark bg-bg-surface hover:bg-bg-elevated'}`}
                                >
                                  {taste ? (
                                    <span className="grid place-items-center shrink-0 h-6 w-6 rounded-md bg-accent text-xs font-bold font-mono uppercase tabular-nums">
                                      {taste}
                                    </span>
                                  ) : (
                                    // Reihe voll — die leere Fläche von früher sah aus wie eine
                                    // vergessene Taste. Ein Strich sagt: dieser hier nur per Klick.
                                    <span className="grid place-items-center shrink-0 h-6 w-6 text-text-muted/50">–</span>
                                  )}
                                  <span className={`min-w-0 truncate text-sm text-text-main ${
                                        istAktiv ? 'font-bold' : 'font-medium'}`}>
                                    {sache}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    </div>
                  )}
            </div>
          </div>
        </div>
        )}
        </div>

        {/* Calendly: bleibt ab dem Cockpit-Start geladen und wird nur ein-/ausgeblendet · beim Closing
            ist „Termin buchen" damit sofort da, ohne Ladezeit mitten im Gespräch. */}
        <div className={`absolute inset-0 overflow-y-auto transition-opacity duration-200 ${
              isBooking ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
          <div className={`w-full max-w-none ${imFenster ? 'px-3 py-2 h-full' : 'px-6 py-3'}`}>
            {/* Im Fenster füllt der Kalender die verfügbare Höhe statt 90 % des BILDSCHIRMS —
                sonst ragte er weit über das Fenster hinaus und man scrollte an ihm entlang. */}
            <div className={`rounded-2xl overflow-hidden border border-border-primary bg-bg-surface ${
                  imFenster ? 'h-full min-h-[24rem]' : 'h-[90vh]'}`}>
              <iframe src={CALENDLY_URL} title="Termin buchen" className="w-full h-full border-0" />
            </div>
          </div>
        </div>
      </div>

      {/* Fortschritt + Keyboard-Hinweise */}
      <div className={`relative border-t border-border-primary bg-bg-surface/40 backdrop-blur-sm ${
            imFenster ? 'px-3 py-2.5' : 'px-6 py-4'}`}>
        {/* Im Fenster gelten die Kürzel nur bei Fokus (s. Kopfnotiz) — die Leiste sagt das, statt
            eine Taste anzubieten, die gerade nichts tut. */}
        {imFenster && !aktiv && (
          <div className="mb-2 text-[11px] text-text-muted">
            Ins Fenster klicken, damit die Tastenkürzel wieder hier greifen.
          </div>
        )}
        <div className="flex gap-1.5 mb-3">
          {flow.map((k, i) => (
            <div
              key={k}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                i === idx ? 'bg-accent' : i < idx ? 'bg-accent/45' : 'bg-text-muted/20'
              }`}
            />
          ))}
        </div>
        {/* Kuerzel links, Zustand rechts — vorher hing der Zustand per `ml-auto` in DERSELBEN
            umbrechenden Zeile: sobald die Kuerzel umbrachen, rutschte „Weiter → …" mit in die
            zweite Reihe und stand mal links, mal rechts. Zwei Spalten halten ihn fest an der
            Kante, wo man ihn sucht. */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11px] text-text-muted">
            <span><Kbd>←</Kbd> <Kbd>→</Kbd> {onBranch && teile.length > 1 ? 'Stufe' : 'blättern'}</span>
            <span><Kbd>G</Kbd> Anfang · <Kbd>⇧G</Kbd> Ende</span>
            {visibleBranches.some((k) => !istAblehnung(data[k]?.title || k)) && (
              <span><Kbd>1</Kbd>…<Kbd>9</Kbd> Einwand</span>
            )}
            {visibleBranches.some((k) => istAblehnung(data[k]?.title || k)) && (
              <span><Kbd>Q</Kbd>…<Kbd>P</Kbd> Ablehnung</span>
            )}
            <span><Kbd>B</Kbd> Termin</span>
            <span><Kbd>N</Kbd> Notiz{notes ? <span className="text-accent"> •</span> : ''}</span>
            {/* Steht nur da, wenn die Taste auch etwas tut: ohne verknüpften fraud0-Lead öffnet M
                nichts, und ein Kürzel in der Leiste, das ins Leere greift, ist schlimmer als keins.
                Der Punkt dahinter meldet dieselbe Sache wie der Haken am Knopf oben — die Mail ist
                raus, ohne dass man den Abschluss dafür öffnen muss. */}
            {mailLeadId && (
              <span><Kbd>M</Kbd> Mail{mailVermerkt ? <span className="text-text-main"> •</span> : ''}</span>
            )}
            <span><Kbd>I</Kbd> Info</span>
            <span><Kbd>+</Kbd> <Kbd>−</Kbd> Schrift</span>
            {/* Die drei, die das Gespraech BEENDEN, hinter einem Trenner: in einer durchgehenden
                Reihe aus zehn gleich gesetzten Kuerzeln lag „Abschluss" optisch neben „Info". */}
            <span className="h-3 w-px bg-border-primary" />
            <span className="text-text-main/70"><Kbd>S</Kbd> Abschluss</span>
            <span><Kbd>X</Kbd> Nicht erreicht</span>
            <span><Kbd>Esc</Kbd> beenden</span>
          </div>
          {outcome ? (
            <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 text-[11px] font-medium text-text-main">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {outcome}
            </span>
          ) : onBranch ? (
            <span className="shrink-0 text-[11px] text-text-muted">
              <Kbd>→</Kbd>&nbsp;{naechsterTeil
                ? <>weiter zu <span className="text-text-main/70">{naechsterTeil.marke ?? 'nächster Stufe'}</span></>
                : 'zurück ins Skript'}
            </span>
          ) : nextTitle ? (
            <span className="shrink-0 max-w-[40%] truncate text-[11px] text-text-muted">
              Weiter&nbsp;→&nbsp;<span className="text-text-main/70">{nextTitle}</span>
            </span>
          ) : null}
        </div>
      </div>

      {/* Bestätigung: Anruf beenden. Vorher fehlte hier jeder Weg, ein Ergebnis zu loggen — X/Esc
          landete direkt im Verwerfen-Dialog, ohne "niemand rangegangen" oder die Outcome/Notiz-Auswahl
          aus dem Abschluss-Panel. Jetzt dieselben zwei Wege wie im Kurzanruf-Dialog. */}
      {mode === 'confirm' && (
        <Overlay onClose={() => setMode('call')}>
          <h2 className="text-lg font-bold mb-1">Anruf beenden?</h2>
          <p className="text-sm text-text-muted mb-4">Wähle, wie der Anruf gewertet wird, oder verwirf ihn ganz.</p>
          <CallbackPicker
            presets={presets} presetIdx={presetIdx} callbackAt={callbackAt} exactOpen={exactOpen}
            hotkeys kannSpeichern
            onPreset={waehlePreset} onExact={waehleGenau}
            onToggleExact={toggleExact} onSavePreset={merkeAlsVorschlag}
          />
          <div className="space-y-2">
            <button
              onClick={quickNoAnswer}
              disabled={saving}
              className="w-full py-3 rounded-xl border border-stark bg-bg-elevated text-text-main text-sm font-semibold hover:bg-bg-surface disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <PhoneOff size={15} /> Niemand rangegangen
              <span className="opacity-70 text-xs">{CALL_OUTCOME_META['NICHT ERREICHT'].hotkey}</span>
            </button>
            <QuickNummerNichtVergeben onClick={quickNummerNichtVergeben} disabled={saving}
                                      phone={aktuelleWahl?.phone ?? null} />
            <button
              onClick={goToSave}
              className="w-full py-3 rounded-xl bg-accent text-sm font-semibold hover:bg-accent/90"
            >
              Als Call tracken (Outcome, Notiz …)
            </button>
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => setMode('call')}
                className="flex-1 py-2.5 rounded-xl border border-border-primary text-sm font-semibold text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors"
              >
                Zurück zum Call <span className="opacity-60 text-xs">Esc</span>
              </button>
              <button
                onClick={verwerfen}
                title={'Der Anruf wird nicht gewertet — und der Wählversuch zurückgenommen:'
                       + ' die Zeile bleibt in der Telefon-Tabelle dort stehen, wo sie war.'}
                className="flex-1 py-2.5 rounded-xl border border-danger/50 text-danger text-sm font-semibold hover:bg-danger/10 transition-colors"
              >
                Verwerfen (nicht tracken) <span className="opacity-70 text-xs">↵</span>
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Kurzer Call: fragen, ob überhaupt getrackt werden soll */}
      {mode === 'shortcall' && (
        <Overlay onClose={() => setMode('call')}>
          <h2 className="text-lg font-bold mb-1">Sehr kurzer Call · {fmt(elapsed)}</h2>
          <p className="text-sm text-text-muted mb-4">Hat jemand abgenommen? Wähle, wie der Anruf gewertet wird.</p>
          <CallbackPicker
            presets={presets} presetIdx={presetIdx} callbackAt={callbackAt} exactOpen={exactOpen}
            hotkeys kannSpeichern
            onPreset={waehlePreset} onExact={waehleGenau}
            onToggleExact={toggleExact} onSavePreset={merkeAlsVorschlag}
          />
          <div className="space-y-2">
            <button
              onClick={quickNoAnswer}
              disabled={saving}
              className="w-full py-3 rounded-xl border border-stark bg-bg-elevated text-text-main text-sm font-semibold hover:bg-bg-surface disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <PhoneOff size={15} /> Nicht rangegangen
              <span className="opacity-70 text-xs">{CALL_OUTCOME_META['NICHT ERREICHT'].hotkey}</span>
            </button>
            <QuickNummerNichtVergeben onClick={quickNummerNichtVergeben} disabled={saving}
                                      phone={aktuelleWahl?.phone ?? null} />
            <button
              onClick={() => setMode('save')}
              className="w-full py-3 rounded-xl bg-accent text-sm font-semibold hover:bg-accent/90"
            >
              Trotzdem als Call tracken <span className="opacity-70 text-xs">↵</span>
            </button>
            <button
              onClick={verwerfen}
              title={'Der Anruf wird nicht gewertet — und der Wählversuch zurückgenommen:'
                     + ' die Zeile bleibt in der Telefon-Tabelle dort stehen, wo sie war.'}
              className="w-full py-2.5 rounded-xl border border-border-primary text-text-muted text-sm hover:text-text-main transition-colors"
            >
              Verwerfen (nicht tracken) <span className="opacity-60 text-xs">Esc</span>
            </button>
          </div>
        </Overlay>
      )}

      {/* Notiz-Overlay */}
      {mode === 'notes' && (
        <Overlay onClose={() => setMode('call')}>
          <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Notiz · <Kbd>Esc</Kbd> schliesst</div>
          <textarea
            ref={notesRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            placeholder="Notizen zum Gespräch..."
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-4 py-3 text-base text-text-main focus:outline-none focus:border-accent resize-none"
          />
        </Overlay>
      )}

      {/* Abschluss-Panel */}
      {mode === 'save' && (
        <Overlay onClose={() => setMode('call')}>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 shrink-0 rounded-full bg-accent/12 text-accent grid place-items-center font-semibold text-sm ring-1 ring-accent/20">
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight">Anruf abschließen</h2>
              <p className="text-sm text-text-muted">
                <span className="font-mono tabular-nums text-accent">{fmt(elapsed)}</span> · {angerufenerName}
              </p>
            </div>
          </div>

          {/* Zwei Blöcke statt einer Reihe: oben, was ein Gespräch war, unten, was keines war.
              Dieselbe Trennung, nach der die Erreichbarkeitsquote rechnet — wer den Knopf im
              unteren Block drückt, sieht damit auch, dass der Anruf nicht als erreicht zählt. */}
          {OUTCOME_GRUPPEN.map((gruppe) => (
            <div key={gruppe.titel}>
              <Label>{gruppe.titel}</Label>
              <div className="flex flex-wrap gap-2 mb-5">
                {gruppe.outcomes.map((o) => {
                  const meta = CALL_OUTCOME_META[o];
                  const selected = outcome === o;
                  return (
                    <button
                      key={o}
                      onClick={() => setOutcome(selected ? null : o)}
                      className={`flex-1 min-w-[8.5rem] py-2.5 px-2 text-sm font-semibold rounded-lg border transition-colors ${
                        selected ? TON_SELECTED[meta.ton] : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
                      }`}
                    >
                      {meta.label} <span className="opacity-60 text-xs">{meta.hotkey}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Was ein Anschluss-Befund bewirkt, steht dabei — sonst ist es ein Knopf, dessen Folge
              man erst am nächsten Tag in der Tabelle bemerkt. Und wenn der Anschluss nicht bekannt
              ist (Cockpit ohne Nummernklick geöffnet) oder die Zentrale gewählt wurde, sagt es das
              ehrlich, statt eine Sperre zu versprechen, die nicht kommt. */}
          {outcome && CALL_OUTCOME_META[outcome].defekt && (
            <p className={`-mt-3 ${zeigeNummernwahl ? 'mb-2' : 'mb-5'} text-xs text-text-muted`}>
              {!aktuelleWahl?.phone_key
                ? 'Ohne angeklickte Nummer lässt sich nicht sagen, welcher Anschluss gemeint ist —'
                  + ' das Ergebnis wird vermerkt, aber keine Nummer gesperrt.'
                : !defektFuer(outcome, aktuelleWahl.person_key)
                  ? 'Die Zentrale wird nicht gesperrt — dort ist nie jemand zuständig, sie ist der Weg'
                    + ' zu den Zuständigen. Das Ergebnis wird trotzdem vermerkt.'
                  : !defektKeys.length
                    ? 'Keine Nummer ausgewählt — das Ergebnis wird vermerkt, aber kein Anschluss'
                      + ' gesperrt.'
                    : CALL_OUTCOME_META[outcome].defekt === 'nicht_zustaendig'
                      ? `${defektNamen} ${mehrereZiele ? 'werden' : 'wird'} für diese Person gesperrt —`
                        + ' andere Nummern des Leads bleiben wählbar.'
                      : `${defektNamen} ${mehrereZiele ? 'werden' : 'wird'} gesperrt — der Wähl-Knopf`
                        + ` verschwindet ${mehrereZiele ? 'dort' : 'an dieser Nummer'}, und es wird keine`
                        + ' Wiedervorlage geplant.'}
            </p>
          )}

          {/* Mehrere Anschlüsse an DIESEM Kontakt: dann ist „die Nummer" keine eindeutige Angabe
              mehr. Am Telefon fällt der Befund oft über eine ANDERE als die gewählte Nummer („die
              Durchwahl gibt es nicht mehr") — ohne diese Wahl bliebe nur die Notiz, und die falsche
              Nummer bliebe wählbar. Vorgewählt ist die gewählte Nummer, der Normalfall bleibt also
              ein Knopfdruck. Erscheint nur, wenn der Befund überhaupt greift (nicht an der
              Zentrale bei „nicht zuständig", siehe defektFuer). */}
          {zeigeNummernwahl && (
            <div className="mb-5">
              <Label>Welche Nummer ist gemeint?</Label>
              <div className="flex flex-col gap-1.5">
                {kontaktNummern.map((p) => {
                  const gewaehlt = defektKeys.includes(p.phone_key);
                  return (
                    <button
                      key={p.phone_key}
                      onClick={() => toggleDefektKey(p.phone_key)}
                      className={`w-full py-2 px-3 text-sm rounded-lg border text-left transition-colors flex items-center gap-2 ${
                        gewaehlt
                          ? 'bg-accent border-accent font-semibold'
                          : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
                      }`}
                    >
                      <span className={`shrink-0 inline-flex items-center justify-center h-4 w-4 rounded border ${
                        gewaehlt ? 'border-current' : 'border-border-primary'
                      }`}>
                        {gewaehlt && <Check size={11} />}
                      </span>
                      <span className="font-semibold">{p.phone}</span>
                      {p.phone_key === aktuelleWahl?.phone_key && (
                        <span className="text-xs opacity-70">gerade gewählt</span>
                      )}
                      {p.label && <span className="text-xs opacity-70">{p.label}</span>}
                      {p.defect_reason && (
                        <span className="ml-auto text-xs opacity-70">schon markiert</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bei WON/LOST wäre ein nächster Anruf Lärm, bei einer toten Nummer ebenso — sonst immer
              sichtbar, auch bei „Nicht erreicht" und bevor ein Outcome gewählt ist. Ziffern gehören
              hier den Emotionen. */}
          {(!outcome || CALL_OUTCOME_META[outcome].wiedervorlage) && (
            <CallbackPicker
              presets={presets} presetIdx={presetIdx} callbackAt={callbackAt} exactOpen={exactOpen}
              hotkeys={false} kannSpeichern
              onPreset={waehlePreset} onExact={waehleGenau}
              onToggleExact={toggleExact} onSavePreset={merkeAlsVorschlag}
            />
          )}

          {/* Ein Schalter, kein zehnter Ergebnis-Knopf: eine Mail ist kein Ausgang des Gesprächs,
              sondern etwas, das man zusätzlich getan hat — nach WON genauso wie nach „Sekretariat".
              Ist der fraud0-Lead weder über die gewählte Nummer noch über `fraud0_lead_id` bekannt,
              sagt die Zeile das, statt einen Vermerk zu versprechen, der nirgends ankommt. */}
          <Label>Mail</Label>
          <div className="mb-5 flex flex-col gap-2">
            {/* Zwei Wege, ein Vermerk: hier schreiben (Pult) oder nur ankreuzen, wenn die Mail
                woanders rausgeht. Geschrieben wird der Verlaufseintrag genau einmal — hat das Pult
                ihn schon gesetzt, ist der Schalter nur noch die Anzeige davon. */}
            <button
              onClick={() => { setMailZurueck('save'); setMode('mail'); }}
              disabled={!mailLeadId}
              className="w-full py-2.5 px-3 text-sm font-semibold rounded-lg border border-accent/50 bg-accent/10 text-accent hover:bg-accent/20 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Pencil size={15} />
              {mailVermerkt ? 'Mail ansehen' : 'Mail schreiben'}
            </button>
            <button
              onClick={() => setMailGeschickt((v) => !v)}
              disabled={!mailLeadId || mailVermerkt}
              className={`w-full py-2.5 px-3 text-sm font-semibold rounded-lg border transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed ${
                mailGeschickt
                  ? 'bg-accent border-accent'
                  : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main disabled:opacity-40'
              }`}
            >
              {mailGeschickt ? <Check size={15} /> : <Mail size={15} />}
              {mailGeschickt ? 'Mail geschickt' : 'Mail geschickt?'}
            </button>
            <p className="text-xs text-text-muted">
              {!mailLeadId
                ? 'Dieser Lead ist mit keinem fraud0-Lead verknüpft — Schreiben und Vermerk bleiben aus.'
                : mailVermerkt
                  ? `Die Mail steht mit Betreff und Text im Verlauf. In zwei Tagen fragt die Telefon-Tabelle, ob ${angerufenerName} geantwortet hat.`
                  : mailGeschickt
                    ? `In zwei Tagen fragt die Telefon-Tabelle, ob ${angerufenerName} geantwortet hat.`
                    : 'Schreiben öffnet das Mail-Pult mit den Vorlagen; ankreuzen genügt, wenn du aus deinem Postfach schickst.'}
            </p>
          </div>

          <Label>Emotion</Label>
          <div className="flex flex-wrap gap-2 mb-5">
            {EMOTIONS.map((e, i) => (
              <button
                key={e}
                onClick={() => toggleEmotion(e)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  emotions.includes(e) ? 'bg-accent/15 border-accent text-accent' : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
                }`}
              >
                {EMOTION_LABEL[e]} <span className="opacity-60 text-xs">{i + 1}</span>
              </button>
            ))}
          </div>

          <Label>Notizen</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Notizen zum Gespräch..."
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent resize-none mb-5"
          />

          {/* Fehlgeschlagenes Speichern SAGEN. Vorher schloss sich das Pult auch dann, und ein
              verlorener Anruf sah aus wie ein gespeicherter (Nr. 13). Die Notizen bleiben stehen,
              der Knopf ist weiter bedienbar — nochmal druecken ist der ganze Weg zurueck. */}
          {speicherFehler && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-bg-elevated border-2 border-stark text-sm font-medium text-text-main">
              Nicht gespeichert. Deine Eingaben stehen noch — bitte nochmal auf „Call speichern".
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={() => save()}
              disabled={saving}
              className="flex-1 py-3 bg-accent rounded-xl font-semibold text-sm hover:bg-accent/90 disabled:opacity-50"
            >
              {saving ? 'Speichern...' : 'Call speichern'} <span className="opacity-70 text-xs">↵</span>
            </button>
            <button onClick={() => setMode('call')} className="px-4 py-3 text-sm text-text-muted hover:text-text-main">
              Zurück <span className="opacity-60 text-xs">Esc</span>
            </button>
          </div>
        </Overlay>
      )}

      {/* Mail-Pult · DASSELBE Bauteil wie auf der fraud0-Karte (Vorlagen, Sie/Du, Betreff, Kopieren,
          Verschickt-Stempel), nur ohne Nachfassen und ohne eigene Wiedervorlage: die steht im
          Abschluss dahinter schon. Verschickt wird auch hier NICHT aus der App — Betreff und Text
          gehen in die Zwischenablage, gesendet wird aus dem eigenen Postfach (Zustellbarkeit und
          Thread-Verlauf, siehe Kopfnotiz in EmailOutreach.tsx). */}
      {mode === 'mail' && (
        <Overlay onClose={mailPultSchliessen} breit>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight">
                {mailZurueck === 'call' ? 'Mail schreiben' : 'Mail nach dem Gespräch'}
              </h2>
              <p className="text-sm text-text-muted truncate">
                {angerufenerName}{liveLead?.company ? ` · ${liveLead.company}` : ''}
              </p>
            </div>
            <button onClick={mailPultSchliessen}
                    className="shrink-0 px-3 py-2 text-sm text-text-muted hover:text-text-main">
              {mailZurueck === 'call' ? 'Zurück ins Skript' : 'Zurück'}
              <span className="opacity-60 text-xs"> Esc</span>
            </button>
          </div>

          {mailFehler ? (
            <p className="text-sm text-text-muted">
              Der Lead konnte nicht geladen werden — das Mail-Pult bleibt zu. Der Anruf lässt sich
              trotzdem abschließen, und der Vermerk „Mail geschickt" steht weiter zur Verfügung.
            </p>
          ) : !mailLead ? (
            <p className="text-sm text-text-muted inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Mail-Pult wird geladen…
            </p>
          ) : (
            <div className={fraud0ThemeClass()}>
              <Suspense fallback={(
                <p className="text-sm text-text-muted inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Mail-Pult wird geladen…
                </p>
              )}>
                <EmailOutreach
                  lead={mailLead}
                  alwaysOpen
                  ohneNachfassen
                  vorwahl="nach_telefonat"
                  personKey={aktuelleWahl?.person_key ?? undefined}
                  portalZiel={popFenster?.document.body ?? null}
                  onPatch={(data) => setMailLead((prev) => (prev ? { ...prev, ...data } : prev))}
                  onSent={(verschickt) => { setMailVermerkt(verschickt); setMailGeschickt(verschickt); }}
                />
              </Suspense>
            </div>
          )}
        </Overlay>
      )}

      {/* Info-Panel: Kontakt & Firmendaten kurz nachschauen oder korrigieren, ohne den Call zu verlassen */}
      {mode === 'info' && liveLead && (
        <Overlay onClose={() => setMode('call')}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold leading-tight">Kontakt & Details</h2>
            <span className="text-sm text-text-muted truncate max-w-[50%]">{liveLead.company}</span>
          </div>

          <Label>Ansprechpartner</Label>
          <input
            type="text"
            value={infoName}
            onChange={(e) => setInfoName(e.target.value)}
            placeholder="Name"
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent mb-3"
          />
          <input
            type="tel"
            value={infoPhone}
            onChange={(e) => setInfoPhone(e.target.value)}
            placeholder="Telefonnummer"
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent mb-3"
          />
          <input
            type="email"
            value={infoEmail}
            onChange={(e) => setInfoEmail(e.target.value)}
            placeholder="E-Mail"
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent mb-5"
          />

          <Label>Firma</Label>
          <div className="space-y-1.5 text-sm mb-5">
            {liveLead.website && (
              <div>
                <a href={liveLead.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline break-all">
                  {liveLead.website}
                </a>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 text-xs rounded bg-bg-elevated border border-border-primary text-text-muted">
                {liveLead.source}
              </span>
              {liveLead.niche && (
                <span className="px-2 py-0.5 text-xs rounded bg-accent/10 text-accent border border-accent/30">{liveLead.niche}</span>
              )}
            </div>
            {liveLead.script_context?.job_title && (
              <div className="pt-1"><span className="text-text-muted text-xs">Jobtitel: </span>{liveLead.script_context.job_title}</div>
            )}
            {liveLead.script_context?.recoverable_eur != null && liveLead.script_context.recoverable_eur > 0 && (
              <div><span className="text-text-muted text-xs">Rückholbar (Schätzung): </span>~{liveLead.script_context.recoverable_eur.toLocaleString('de-DE')} €/Jahr</div>
            )}
          </div>

          {calls.filter((c) => c.lead_id === activeLead.id).length > 0 && (
            <>
              <Label>Letzte Calls</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1 mb-5">
                {calls.filter((c) => c.lead_id === activeLead.id).slice(0, 3).map((c, i) => (
                  <div key={c.id ?? i} className="border-l-2 border-accent/30 pl-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-text-main">{c.outcome ?? '-'}</span>
                      <span className="text-text-muted text-xs">
                        {new Date(c.date).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {c.notes && <div className="text-text-muted italic mt-0.5">"{c.notes}"</div>}
                    <AufnahmeStatusChip callId={c.id} />
                  </div>
                ))}
              </div>
            </>
          )}

          {infoError && <p className="text-sm text-danger mb-3">{infoError}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={saveInfo}
              disabled={savingInfo}
              className="flex-1 py-3 bg-accent rounded-xl font-semibold text-sm hover:bg-accent/90 disabled:opacity-50"
            >
              {savingInfo ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setMode('call')} className="px-4 py-3 text-sm text-text-muted hover:text-text-main">
              Zurück <span className="opacity-60 text-xs">Esc</span>
            </button>
          </div>
        </Overlay>
      )}

      {/* Die Anfasser zum Groessenaendern — dieselben wie am Details- und Notiz-Fenster (2026-08-12).
          Vorher hing das am nativen CSS-Griff: ein 15-Pixel-Dreieck in der aeussersten Ecke, auf
          dunklem Grund praktisch unsichtbar. */}
      {imFenster && <FensterGriffe griffe={griffe} />}
    </div>
  );

  // Ausgelagert haengt derselbe Baum im Dokument des anderen Fensters. Kein zweiter React-Baum,
  // keine Kopie des Zustands: der Anruf, der Timer und die Skript-Position sind dieselben — nur
  // das DOM steht woanders. Deshalb ueberlebt ein laufendes Gespraech den Wechsel in beide
  // Richtungen, ohne dass irgendetwas gerettet werden muesste.
  return ausgelagert ? (
    <>
      {marke(() => popFenster?.focus(),
             `Anruf mit ${angerufenerName} läuft im eigenen Fenster · nach vorn holen`)}
      <AusgelagerterInhalt fenster={popFenster}>{rahmen}</AusgelagerterInhalt>
    </>
  ) : rahmen;
}

// --- kleine UI-Helfer ---
function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="inline-block min-w-[1.5em] text-center px-1.5 py-0.5 rounded-md border border-border-primary bg-bg-elevated text-[0.7rem] font-mono text-text-main shadow-[0_1px_0_rgba(0,0,0,0.06)]">{children}</kbd>;
}
function Label({ children }: { children: React.ReactNode }) {
  return <div className="text-xs uppercase tracking-wider text-text-muted mb-2">{children}</div>;
}

// „Nummer nicht vergeben" als ein Knopfdruck — in BEIDEN ersten Dialogen („Anruf beenden?" und
// „Sehr kurzer Call"), denn welcher davon aufgeht, entscheidet allein die Gesprächsdauer. Ein toter
// Anschluss ist derselbe Befund, egal ob man nach 5 oder nach 40 Sekunden auflegt.
//
// Ein Bauteil statt zweimal derselbe Knopf: die beiden Dialoge sind schon heute fast wortgleich, und
// die nächste Änderung an der Beschriftung soll nicht an einer der zwei Stellen hängenbleiben.
//
// Rot ist dieselbe Farbe, in der ein Anschluss-Befund im Abschluss-Panel und in der Telefon-Tabelle
// steht (TON_SELECTED.defekt) — derselbe Befund, dasselbe Signal.
function QuickNummerNichtVergeben({ onClick, disabled, phone }: {
  onClick: () => void; disabled: boolean; phone: string | null;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      // Was der Knopf TUT, steht dran — er sperrt eine Nummer, und zwar genau die gewählte. Ohne
      // Wählzusammenhang wird nur das Ergebnis vermerkt; das zu verschweigen wäre ein Versprechen,
      // das nicht eingelöst wird (dieselbe Regel wie im Abschluss-Panel).
      title={phone
        ? `${phone} wird gesperrt. Ist eine ANDERE Nummer des Kontakts gemeint, führt der Weg über`
          + ' „Als Call tracken" — dort steht die Nummernwahl.'
        : 'Ohne angeklickte Nummer wird das Ergebnis nur vermerkt, kein Anschluss gesperrt.'}
      className="w-full py-3 rounded-xl border-2 border-stark bg-transparent text-text-main text-sm font-semibold hover:bg-bg-elevated disabled:opacity-50 flex items-center justify-center gap-2"
    >
      <Ban size={15} /> {CALL_OUTCOME_META['NUMMER NICHT VERGEBEN'].label}
      <span className="opacity-70 text-xs">{CALL_OUTCOME_META['NUMMER NICHT VERGEBEN'].hotkey}</span>
    </button>
  );
}
// Abschluss, Notiz, Info, Bestätigung. `absolute` statt `fixed`, seit das Cockpit auch ein Fenster
// sein kann: die Panels gehören INS Cockpit, nicht über den ganzen Bildschirm. Im Vollbild ändert
// das nichts — dort ist die Wurzel `fixed inset-0` und damit selbst der Bezugsrahmen. Als Fenster
// wäre die alte Fassung ein Vollbild-Vorhang gewesen, der genau das kaputt macht, wofür es das
// Fenster gibt: nebenher etwas nachschlagen.
// `breit` ist für das Mail-Pult: Betreff, Vorlagen-Chips und ein zwölfzeiliges Textfeld werden in
// der Abschluss-Breite zur Spalte. Die übrigen Panels bleiben schmal — dort ist Breite nur Weg.
function Overlay({ children, onClose, breit }: { children: React.ReactNode; onClose: () => void; breit?: boolean }) {
  return (
    <div
      className="absolute inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${breit ? 'max-w-2xl' : 'max-w-lg'} my-auto bg-bg-surface/95 backdrop-blur-xl border border-border-primary rounded-2xl p-6 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
